import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Sparkles, RotateCcw, Home, Heart, Leaf, Eye, Star, Target, AlertTriangle, Printer, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import ofnrData from '../data/ofnr_trainer_data.json';
import ConflictNavigator from './ConflictNavigator';
import GratitudeBuilder from './GratitudeBuilder';
import QuadruneBrainGame from './QuadruneBrainGame';
import ConnectionCards from './ConnectionCards';
import MeaningMaker from './MeaningMaker';
import TruthCourageBuilder from './TruthCourageBuilder';

function OFNRTrainer() {
  const [searchParams] = useSearchParams();
  const initialTab = parseInt(searchParams.get('tab')) || 0;
  const [currentPhase, setCurrentPhase] = useState(initialTab); // 0 = intro, 1-5 = phases
  const [selectedFeelings, setSelectedFeelings] = useState([]);
  const [selectedNeeds, setSelectedNeeds] = useState([]);
  const [selectedSatisfiedFeelings, setSelectedSatisfiedFeelings] = useState([]);
  const [selectedUnsatisfiedFeelings, setSelectedUnsatisfiedFeelings] = useState([]);
  const [selectedNonFeelings, setSelectedNonFeelings] = useState([]);
  const [scenario, setScenario] = useState('');
  const [userInput, setUserInput] = useState('');
  const [flashTan, setFlashTan] = useState(false);
  const [activeGame, setActiveGame] = useState(null); // For launching mini-games
  
  const phases = [
    { id: 0, title: 'Introduction', color: 'gray', description: 'Learn the color-coded system' },
    { id: 1, title: 'O - Observations', color: 'blue', description: 'Oceanic blue - see clearly like the sea' },
    { id: 2, title: 'F - Feelings', color: 'red', description: 'Red - what is alive in you, like blood coursing' },
    { id: 3, title: 'N - Needs', color: 'green', description: 'Green - what helps you grow most right now' },
    { id: 4, title: 'R - Requests', color: 'yellow', description: 'Gold - finding common ground, even tiny nuggets' },
    { id: 5, title: '⚠️ Non-Feelings', color: 'amber', description: 'Tan - trap words that blame others' },
    { id: 6, title: '🎮 Games & Tools', color: 'purple', description: 'Advanced practices and multiplayer games' }
  ];

  // Print functionality
  const printCurrentSection = () => {
    const currentPhaseName = phases[currentPhase]?.title || 'OFNR Trainer';
    
    // Create print content
    let printContent = `
      <html>
      <head>
        <title>${currentPhaseName} - The Belonging Sessions</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; color: #333; }
          h1 { color: #2563eb; border-bottom: 3px solid #2563eb; padding-bottom: 10px; }
          h2 { color: #dc2626; margin-top: 30px; }
          h3 { color: #059669; margin-top: 20px; }
          .word-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 20px 0; }
          .word-item { border: 1px solid #ccc; padding: 8px; text-align: center; background: #f9f9f9; }
          .selected { background: #3b82f6; color: white; }
          .example { background: #e0f2fe; padding: 15px; margin: 15px 0; border-left: 4px solid #0277bd; }
          .instruction { background: #f3e5f5; padding: 15px; margin: 15px 0; border-left: 4px solid #7b1fa2; }
          .scenario { background: #fff3e0; padding: 15px; margin: 15px 0; border-left: 4px solid #ef6c00; font-style: italic; }
          @page { margin: 1in; }
          @media print { .no-print { display: none !important; } }
        </style>
      </head>
      <body>
        <h1>${currentPhaseName}</h1>
        <p><strong>The Belonging Sessions - OFNR Trainer</strong></p>
        <p><em>Printed: ${new Date().toLocaleString()}</em></p>
    `;
    
    if (currentPhase === 0) {
      printContent += `
        <h2>Color-Coded System</h2>
        <div style="display: grid; grid-template-columns: 1fr; gap: 15px; margin: 20px 0;">
          <div style="border-left: 8px solid #2563eb; padding: 15px; background: #eff6ff;">
            <h3>🔵 O - Observations (Oceanic Blue)</h3>
            <p>See clearly like observing the sea. Express yourself honestly without blaming or criticizing.</p>
          </div>
          <div style="border-left: 8px solid #dc2626; padding: 15px; background: #fef2f2;">
            <h3>🔴 F - Feelings (Red)</h3>
            <p>What is alive in you, like blood coursing through you. Your emotions, life force.</p>
          </div>
          <div style="border-left: 8px solid #059669; padding: 15px; background: #f0fdf4;">
            <h3>🟢 N - Needs (Green)</h3>
            <p>What do you need to grow most in this moment, like a verdant tree.</p>
          </div>
          <div style="border-left: 8px solid #eab308; padding: 15px; background: #fffbeb;">
            <h3>🟡 R - Requests (Gold)</h3>
            <p>Requesting of each other until you find common ground - even tiny nuggets matter.</p>
          </div>
          <div style="border-left: 8px solid #b45309; padding: 15px; background: #fffbeb;">
            <h3>🟤 Non-Feeling Words (Tan)</h3>
            <p>Words that blame others and give away your power. Watch out for these trap words.</p>
          </div>
        </div>
      `;
    }
    
    if (currentPhase === 1) {
      printContent += `
        <h2>Observations - Oceanic Blue</h2>
        <div class="instruction">
          <strong>What is an Observation?</strong>
          <ul>
            <li>What you could videotape or audio record</li>
            <li>Facts without interpretation or judgment</li>
            <li>Creates shared reality with the other person</li>
            <li>Free from evaluation</li>
          </ul>
        </div>
        <div class="example">
          <strong>✅ Pure Observations:</strong><br>
          "When you walked in without saying hello..."<br>
          "When I heard your voice get louder than usual..."<br>
          "When I saw the dishes still in the sink at 8pm..."
        </div>
        <div class="example" style="background: #ffebee;">
          <strong>❌ Evaluations (Avoid):</strong><br>
          "When you were rude..."<br>
          "When you got angry at me..."<br>
          "When you were being lazy..."
        </div>
      `;
    }
    
    if (currentPhase === 2 && selectedFeelings.length > 0) {
      printContent += `
        <h2>Your Selected Feelings</h2>
        <div class="word-grid">
          ${selectedFeelings.map(f => `<div class="word-item selected">${f}</div>`).join('')}
        </div>
        <p><em>Total feelings selected: ${selectedFeelings.length}</em></p>
      `;
    }
    
    if (currentPhase === 3 && selectedNeeds.length > 0) {
      printContent += `
        <h2>Your Selected Needs</h2>
        <div class="word-grid">
          ${selectedNeeds.map(n => `<div class="word-item selected">${n}</div>`).join('')}
        </div>
        <p><em>Total needs selected: ${selectedNeeds.length}</em></p>
      `;
    }
    
    if (scenario) {
      printContent += `
        <div class="scenario">
          <strong>Practice Scenario:</strong><br>
          "${scenario}"
        </div>
      `;
    }
    
    printContent += `
        <hr style="margin: 30px 0;">
        <p style="text-align: center; font-size: 12px; color: #666;">
          © 2025 The Belonging Sessions at Defiance Springs<br>
          Learn more at belonging-sessions.preview.emergentagent.com
        </p>
      </body>
      </html>
    `;
    
    // Open print window
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
    
    toast.success(`${currentPhaseName} section ready for printing!`);
  };

  const generateScenario = async () => {
    // Use M.ai.ry to generate scenarios
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/scenario`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'ofnr_practice',
          context: 'body_sensation_scenario',
          phase: currentPhase
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setScenario(data.scenario);
      } else {
        // Fallback scenarios
        const scenarios = [
          "when you walked out of the room while I was talking, I felt frozen and weak",
          "when I heard your voice get louder, I noticed my chest tightening and felt small",
          "when you didn't respond to my text, I felt a sinking sensation in my stomach",
          "when you smiled at me across the room, I felt warmth spreading through my whole body",
          "when the meeting ended without my idea being heard, I felt deflated and heavy",
          "when you interrupted me for the third time, I felt my jaw clench and energy drain away",
          "when I saw you helping that stranger, I felt a lightness in my chest and smiled involuntarily"
        ];
        setScenario(scenarios[Math.floor(Math.random() * scenarios.length)]);
      }
    } catch (error) {
      const scenarios = [
        "when you walked out of the room while I was talking, I felt frozen and weak",
        "when I heard your voice get louder, I noticed my chest tightening and felt small",
        "when you didn't respond to my text, I felt a sinking sensation in my stomach"
      ];
      setScenario(scenarios[Math.floor(Math.random() * scenarios.length)]);
    }
  };

  useEffect(() => {
    generateScenario();
  }, [currentPhase]);

  const nineBasicNeeds = [
    "1. Sustenance", "2. Safety", "3. Love", 
    "4. Empathy", "5. Rest, Recreation & Play", "6. Community",
    "7. Creativity", "8. Autonomy", "9. Meaning or Purpose in Life"
  ];

  const feelingWords = [
    // When needs satisfied (red with warm tones)
    "joyful", "grateful", "peaceful", "excited", "confident", "loving", "hopeful", "content", "inspired", "energetic",
    // When needs not satisfied (red with cool tones)  
    "angry", "frustrated", "sad", "afraid", "hurt", "lonely", "disappointed", "anxious", "irritated", "overwhelmed"
  ];

  const needWords = [
    "connection", "understanding", "respect", "autonomy", "safety", "love", "growth", "creativity", "peace", "belonging",
    "support", "trust", "acceptance", "freedom", "meaning", "beauty", "fun", "rest", "community", "honesty"
  ];

  const nonFeelingWords = [
    "abandoned", "betrayed", "rejected", "ignored", "misunderstood", "attacked", "criticized", "judged", 
    "manipulated", "controlled", "used", "taken advantage of", "unheard", "unseen", "unloved"
  ];

  const handleNonFeelingClick = (word) => {
    setFlashTan(true);
    setTimeout(() => setFlashTan(false), 500);
  };

  // Game launcher functions
  if (activeGame === 'conflict') {
    return <ConflictNavigator onBack={() => setActiveGame(null)} />;
  }
  if (activeGame === 'gratitude') {
    return <GratitudeBuilder onBack={() => setActiveGame(null)} />;
  }
  if (activeGame === 'brain') {
    return <QuadruneBrainGame onBack={() => setActiveGame(null)} />;
  }
  if (activeGame === 'connection') {
    return <ConnectionCards onBack={() => setActiveGame(null)} />;
  }
  if (activeGame === 'meaning') {
    return <MeaningMaker onBack={() => setActiveGame(null)} />;
  }
  if (activeGame === 'courage') {
    return <TruthCourageBuilder onBack={() => setActiveGame(null)} />;
  }

  return (
    <div className={`min-h-screen transition-all duration-500 ${flashTan ? 'bg-yellow-600' : ''}`}>
      
      {/* Background */}
      <div 
        className="min-h-screen relative"
        style={{
          background: currentPhase === 0 ? 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' :
                     currentPhase === 1 ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' :
                     currentPhase === 2 ? 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)' :
                     currentPhase === 3 ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' :
                     currentPhase === 4 ? 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)' :
                     currentPhase === 5 ? 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)' :
                     `url('https://customer-assets.emergentagent.com/job_belonging-sessions/artifacts/femlgvnm_ChatGPT%20Image%20Sep%2011%2C%202025%20at%2012_35_28%20AM.jpeg') center/cover`
        }}
      >
        
        {/* Header */}
        <header className="py-4 md:py-8 px-4 border-b-4 border-black bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="text-center md:text-left">
                <h1 className="text-2xl md:text-4xl font-bold">Belonging Sessions Trainer</h1>
                <p className="text-sm md:text-lg text-gray-600">Universal tool for compassionate communication</p>
              </div>
              <div className="flex gap-2 md:gap-3">
                <a href="/belonging-ebook">
                  <Button variant="outline" className="border-black text-sm md:text-base px-3 md:px-4 py-2" style={{ borderRadius: 0 }}>
                    <ArrowLeft className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                    Practice eBook
                  </Button>
                </a>
                <Button 
                  onClick={printCurrentSection}
                  className="bg-gray-600 text-white hover:bg-gray-700 text-sm md:text-base px-3 md:px-4 py-2"
                  style={{ borderRadius: 0 }}
                >
                  <Printer className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                  Print
                </Button>
                <a href="/">
                  <Button variant="outline" className="border-black text-sm md:text-base px-3 md:px-4 py-2" style={{ borderRadius: 0 }}>
                    <Home className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                    Home
                  </Button>
                </a>
              </div>
            </div>
            
            {/* Mobile-Optimized Phase Navigation */}
            <div className="mt-4 md:mt-8 flex gap-1 md:gap-2 overflow-x-auto relative">
              {phases.map((phase) => (
                <Button
                  key={phase.id}
                  onClick={() => setCurrentPhase(phase.id)}
                  className={`px-2 md:px-6 py-2 md:py-3 border-2 border-black text-xs md:text-base whitespace-nowrap ${
                    currentPhase === phase.id 
                      ? phase.id === 0 ? 'bg-black text-white' :
                        phase.id === 1 ? 'bg-blue-600 text-white' :
                        phase.id === 2 ? 'bg-red-600 text-white' :
                        phase.id === 3 ? 'bg-green-600 text-white' :
                        phase.id === 4 ? 'bg-yellow-500 text-black' :
                        phase.id === 5 ? 'bg-amber-700 text-white' :
                        'bg-purple-600 text-white'
                      : phase.id === 0 ? 'bg-gray-200 text-black hover:bg-gray-300' :
                        phase.id === 1 ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' :
                        phase.id === 2 ? 'bg-red-100 text-red-800 hover:bg-red-200' :
                        phase.id === 3 ? 'bg-green-100 text-green-800 hover:bg-green-200' :
                        phase.id === 4 ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' :
                        phase.id === 5 ? 'bg-amber-100 text-amber-800 hover:bg-amber-200' :
                        'bg-purple-100 text-purple-800 hover:bg-purple-200'
                  }`}
                  style={{ borderRadius: 0 }}
                >
                  {phase.title}
                </Button>
              ))}
            </div>
            
            {/* Fun Handwritten Arrow Pointer to Needs Tab */}
            <div className="mt-6 mb-2 flex justify-start ml-[40%]">
              <div className="relative bg-green-100 border-2 border-green-600 rounded-lg px-6 py-3 transform rotate-1 shadow-lg">
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2">
                  <svg width="100" height="50" viewBox="0 0 100 50">
                    <path 
                      d="M 50 45 Q 45 25, 50 10" 
                      stroke="#059669" 
                      strokeWidth="3" 
                      fill="none" 
                      strokeLinecap="round"
                      strokeDasharray="5,3"
                    />
                    <polygon 
                      points="50,10 47,18 53,18" 
                      fill="#059669"
                    />
                  </svg>
                </div>
                <p 
                  className="text-green-800 font-bold text-lg text-center"
                  style={{ fontFamily: "'Comic Sans MS', 'Brush Script MT', cursive" }}
                >
                  Feelings & Needs Exercise
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="py-6 md:py-12 px-4">
          <div className="max-w-6xl mx-auto px-2 md:px-0">
            
            {/* Introduction Page - Mobile Optimized */}
            {currentPhase === 0 && (
              <div className="space-y-6 md:space-y-8">
                <div className="text-center mb-8 md:mb-12">
                  <h2 className="text-3xl md:text-5xl font-bold mb-4 md:mb-6">The Language of Belonging</h2>
                  <p className="text-lg md:text-2xl text-gray-700 max-w-4xl mx-auto px-4">
                    Learn compassionate communication through colors, like a beautiful traffic light for your heart
                  </p>
                </div>

                {/* Key Insight: Needs are Core Elements */}
                <div className="bg-gradient-to-r from-green-100 to-emerald-100 border-4 border-green-600 rounded-lg p-6 mb-8 max-w-4xl mx-auto">
                  <h3 className="text-2xl font-bold text-green-800 mb-4 text-center">🌱 Understanding Needs</h3>
                  <p className="text-green-900 leading-relaxed text-lg mb-4">
                    <span className="font-bold">Needs</span> are the <span className="font-semibold">core elemental nonnegotiable components, values, and requirements</span> of a fulfilled, vibrant life.
                  </p>
                  <p className="text-green-800 leading-relaxed">
                    When your body has a reaction—including <span className="font-semibold text-green-700">good</span> reactions—it is beneficial to reflect on <span className="font-semibold">why that is happening</span>. Almost always it relates to one of these non-negotiable core elements of life.
                  </p>
                  <p className="text-green-900 mt-4 font-semibold italic text-center">
                    Think about the feeling you're having, what's alive in you, and which core element it relates to—that is the point of the Feelings and Needs components.
                  </p>
                </div>

                {/* Mobile-Responsive Color System */}
                <Card className="border-4 border-black bg-white" style={{ borderRadius: 0, boxShadow: '8px 8px 0px rgba(0,0,0,0.2)' }}>
                  <CardHeader className="bg-gray-50 text-center p-4 md:p-8">
                    <CardTitle className="text-xl md:text-3xl">OFNR Color System</CardTitle>
                    <p className="text-sm md:text-lg text-gray-600">Your guide to compassionate communication</p>
                  </CardHeader>
                  <CardContent className="p-4 md:p-8">
                    <div className="space-y-4 md:space-y-8">
                      
                      {/* O - Observations */}
                      <div 
                        className="flex items-center gap-6 p-6 rounded border-l-8 border-blue-600 bg-blue-50 cursor-pointer transition-all hover:scale-105"
                        onClick={() => {
                          // Play oceanic tone (C note)
                          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+LyvmwhBjuBzvLZiTgJGGS57+OiUxELTqXh8bViFQU+jNXz0YAqBSl+zPDajkILElyx6OyrWBUIQ5zd8sFuIAU8ltryxnkpBSl8yO/ekkMLEV6z5+2qVxMMSKPi8LJjGAU9k9n0x3QlBTuAzfPXkjgJGWS57+OiUxELTqXg8bViFQU+jNXz0YAqBSl+zPDajkILElyx6OyrWBUIQ5zd8sFuIAU8ltryxnkpBSl8yO/ekkMLEV6z5+2qVxMMSKPi8LJjGAU9k9n0x3QlBTuAzfPXkjcIGWO67+SiUxILTKPj8LJkGQU9k9j0yHQlBTuAzfPXkjcIGWO67+SiUxILTKPj8LJkGQU9k9j0yHQlBTuAzfPXkjcIGWO67+SiUxILTKPj8LJkGQU9k9j0yHQlBTuAzfPXkjcIGWO67+SiUxILTKPj8LJkGQU9k9j0yHQlBTuAzfPXkjcIGWO67+SiUxILTKPj8LJkGQU9k9j0yA==');
                          audio.volume = 0.3;
                          audio.play().catch(() => {});
                        }}
                      >
                        <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center">
                          <Eye className="w-8 h-8 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-blue-800">O - Observations</h3>
                          <p className="text-lg text-blue-700">
                            <strong>Oceanic Blue</strong> - See clearly like observing the sea
                          </p>
                          <p className="text-base text-gray-700">
                            Express yourself honestly through statements without blaming or criticizing
                          </p>
                        </div>
                      </div>

                      {/* F - Feelings */}
                      <div 
                        className="flex items-center gap-6 p-6 rounded border-l-8 border-red-600 bg-red-50 cursor-pointer transition-all hover:scale-105"
                        onClick={() => {
                          // Play warm tone (D note)
                          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+LyvmwhBjuBzvLZiTgJGGS57+OiUxELTqXh8bViFQU+jNXz0YAqBSl+zPDajkILElyx6OyrWBUIQ5zd8sFuIAU8ltryxnkpBSl8yO/ekkMLEV6z5+2qVxMMSKPi8LJjGAU9k9n0x3QlBTuAzfPXkjgJGWS57+OiUxELTqXg8bViFQU+jNXz0YAqBSl+zPDajkILElyx6OyrWBUIQ5zd8sFuIAU8ltryxnkpBSl8yO/ekkMLEV6z5+2qVxMMSKPi8LJjGAU9k9n0x3QlBTuAzfPXkjcIGWO67+SiUxILTKPj8LJkGQU9k9j0yHQlBTuAzfPXkjcIGWO67+SiUxILTKPj8LJkGQU9k9j0yHQlBTuAzfPXkjcIGWO67+SiUxILTKPj8LJkGQU9k9j0yHQlBTuAzfPXkjcIGWO67+SiUxILTKPj8LJkGQU9k9j0yHQlBTuAzfPXkjcIGWO67+SiUxILTKPj8LJkGQU9k9j0yA==');
                          audio.volume = 0.4;
                          audio.play().catch(() => {});
                        }}
                      >
                        <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center">
                          <Heart className="w-8 h-8 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-red-800">F - Feelings</h3>
                          <p className="text-lg text-red-700">
                            <strong>Red</strong> - What is alive in you, like blood coursing through you
                          </p>
                          <p className="text-base text-gray-700">
                            Your emotions, the chemistry within your bloodstream, what you're experiencing
                          </p>
                        </div>
                      </div>

                      {/* Epic Hand-Drawn Arrow: Feelings ↔ Needs Connection */}
                      <div className="relative py-8">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <svg width="300" height="120" viewBox="0 0 300 120" className="transform rotate-0">
                            {/* Curved bidirectional arrows */}
                            <path 
                              d="M 50 30 Q 150 10, 250 30" 
                              stroke="#dc2626" 
                              strokeWidth="4" 
                              fill="none" 
                              strokeLinecap="round"
                              strokeDasharray="8,4"
                              className="animate-pulse"
                            />
                            <path 
                              d="M 250 90 Q 150 110, 50 90" 
                              stroke="#16a34a" 
                              strokeWidth="4" 
                              fill="none" 
                              strokeLinecap="round"
                              strokeDasharray="8,4"
                              className="animate-pulse"
                            />
                            {/* Arrow heads */}
                            <polygon points="250,30 245,25 245,35" fill="#dc2626"/>
                            <polygon points="50,90 55,85 55,95" fill="#16a34a"/>
                          </svg>
                        </div>
                        <div className="bg-amber-100 border-4 border-amber-600 rounded-lg p-6 relative z-10 mx-auto max-w-2xl transform rotate-1 shadow-xl">
                          <div 
                            className="text-center text-amber-900 font-bold text-xl mb-3"
                            style={{ fontFamily: "'Comic Sans MS', 'Brush Script MT', cursive" }}
                          >
                            🎯 KEY INSIGHT! The Feelings ↔ Needs Connection
                          </div>
                          <p className="text-amber-800 leading-relaxed text-center">
                            <span className="text-red-700 font-semibold">Feelings</span> exist to tell you which <span className="text-green-700 font-semibold">Needs</span> are met or unmet. 
                            <span className="text-green-700 font-semibold"> Needs</span> being met/unmet cause <span className="text-red-700 font-semibold">Feelings</span>. 
                            They dance together! Notice this circular relationship—it's the heart of NVC.
                          </p>
                          <p className="text-amber-700 text-sm text-center mt-3 italic">
                            (This works for joy too! Ecstasy = a core need being deeply met 😊)
                          </p>
                        </div>
                      </div>

                      {/* N - Needs */}
                      <div 
                        className="flex items-center gap-6 p-6 rounded border-l-8 border-green-600 bg-green-50 cursor-pointer transition-all hover:scale-105"
                        onClick={() => {
                          // Play growth tone (E note)
                          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+LyvmwhBjuBzvLZiTgJGGS57+OiUxELTqXh8bViFQU+jNXz0YAqBSl+zPDajkILElyx6OyrWBUIQ5zd8sFuIAU8ltryxnkpBSl8yO/ekkMLEV6z5+2qVxMMSKPi8LJjGAU9k9n0x3QlBTuAzfPXkjgJGWS57+OiUxELTqXg8bViFQU+jNXz0YAqBSl+zPDajkILElyx6OyrWBUIQ5zd8sFuIAU8ltryxnkpBSl8yO/ekkMLEV6z5+2qVxMMSKPi8LJjGAU9k9n0x3QlBTuAzfPXkjcIGWO67+SiUxILTKPj8LJkGQU9k9j0yHQlBTuAzfPXkjcIGWO67+SiUxILTKPj8LJkGQU9k9j0yHQlBTuAzfPXkjcIGWO67+SiUxILTKPj8LJkGQU9k9j0yHQlBTuAzfPXkjcIGWO67+SiUxILTKPj8LJkGQU9k9j0yA==');
                          audio.volume = 0.35;
                          audio.play().catch(() => {});
                        }}
                      >
                        <div className="w-16 h-16 rounded-full bg-green-600 flex items-center justify-center">
                          <Leaf className="w-8 h-8 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-green-800">N - Needs</h3>
                          <p className="text-lg text-green-700">
                            <strong>Green</strong> - What do you need to grow most in this moment
                          </p>
                          <p className="text-base text-gray-700">
                            Your values, what helps you find purpose and connection, like a verdant tree
                          </p>
                        </div>
                      </div>

                      {/* R - Requests */}
                      <div 
                        className="flex items-center gap-6 p-6 rounded border-l-8 border-yellow-500 bg-yellow-50 cursor-pointer transition-all hover:scale-105"
                        onClick={() => {
                          // Play golden tone (G note)
                          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+LyvmwhBjuBzvLZiTgJGGS57+OiUxELTqXh8bViFQU+jNXz0YAqBSl+zPDajkILElyx6OyrWBUIQ5zd8sFuIAU8ltryxnkpBSl8yO/ekkMLEV6z5+2qVxMMSKPi8LJjGAU9k9n0x3QlBTuAzfPXkjgJGWS57+OiUxELTqXg8bViFQU+jNXz0YAqBSl+zPDajkILElyx6OyrWBUIQ5zd8sFuIAU8ltryxnkpBSl8yO/ekkMLEV6z5+2qVxMMSKPi8LJjGAU9k9n0x3QlBTuAzfPXkjcIGWO67+SiUxILTKPj8LJkGQU9k9j0yHQlBTuAzfPXkjcIGWO67+SiUxILTKPj8LJkGQU9k9j0yHQlBTuAzfPXkjcIGWO67+SiUxILTKPj8LJkGQU9k9j0yHQlBTuAzfPXkjcIGWO67+SiUxILTKPj8LJkGQU9k9j0yA==');
                          audio.volume = 0.45;
                          audio.play().catch(() => {});
                        }}
                      >
                        <div className="w-16 h-16 rounded-full bg-yellow-500 flex items-center justify-center">
                          <Target className="w-8 h-8 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-yellow-700">R - Requests</h3>
                          <p className="text-lg text-yellow-600">
                            <strong>Gold</strong> - Requesting of each other until you find common ground
                          </p>
                          <p className="text-base text-gray-700">
                            Win-win solutions, even if tiny - just a little nugget is all that matters
                          </p>
                        </div>
                      </div>

                      {/* Non-Feelings Warning */}
                      <div 
                        className="flex items-center gap-6 p-6 rounded border-l-8 border-amber-700 bg-amber-100 cursor-pointer transition-all hover:scale-105"
                        onClick={() => {
                          // Play warning tone (lower F note)
                          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+LyvmwhBjuBzvLZiTgJGGS57+OiUxELTqXh8bViFQU+jNXz0YAqBSl+zPDajkILElyx6OyrWBUIQ5zd8sFuIAU8ltryxnkpBSl8yO/ekkMLEV6z5+2qVxMMSKPi8LJjGAU9k9n0x3QlBTuAzfPXkjgJGWS57+OiUxELTqXg8bViFQU+jNXz0YAqBSl+zPDajkILElyx6OyrWBUIQ5zd8sFuIAU8ltryxnkpBSl8yO/ekkMLEV6z5+2qVxMMSKPi8LJjGAU9k9n0x3QlBTuAzfPXkjcIGWO67+SiUxILTKPj8LJkGQU9k9j0yHQlBTuAzfPXkjcIGWO67+SiUxILTKPj8LJkGQU9k9j0yHQlBTuAzfPXkjcIGWO67+SiUxILTKPj8LJkGQU9k9j0yHQlBTuAzfPXkjcIGWO67+SiUxILTKPj8LJkGQU9k9j0yA==');
                          audio.volume = 0.2;
                          audio.play().catch(() => {});
                        }}
                      >
                        <div className="w-16 h-16 rounded-full bg-amber-700 flex items-center justify-center">
                          <AlertTriangle className="w-8 h-8 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-amber-800">⚠️ Non-Feeling Words</h3>
                          <p className="text-lg text-amber-700">
                            <strong>Tan</strong> - Words that blame others and give away your power
                          </p>
                          <p className="text-base text-gray-700">
                            Watch out for these trap words that imply someone else is doing something to you
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-12 text-center">
                      <p className="text-xl text-gray-800 mb-6">
                        "Think about these tools when you're in conflict-related conversations. 
                        The test isn't duplicating what we're saying - the test is: 
                        <strong>Can you apply this in real-world situations?</strong>"
                      </p>

                      {/* Compassion for Joy AND Conflict - Fractal Insight */}
                      <div className="bg-gradient-to-r from-rose-100 to-pink-100 border-4 border-rose-500 rounded-lg p-6 mb-6">
                        <h4 className="text-xl font-bold text-rose-800 mb-3 text-center">💝 Not Just for Conflict!</h4>
                        <p className="text-rose-900 leading-relaxed mb-3">
                          Compassionate communication isn't just for conflict. In fact, it's probably <span className="font-semibold">equally useful</span> to recognize, appreciate, and provide gratitude for the consciousness of what's alive in us when things are going well too!
                        </p>
                        <p className="text-rose-800 leading-relaxed mb-3">
                          Each of these contemplations is <span className="font-semibold">fractal</span>—it applies to the way we contemplate ourselves, those closest to us, our broader family, and all families combined into one vast "family."
                        </p>
                        <p className="text-rose-900 font-semibold text-center italic text-lg">
                          Compassionate interchange through the good and the bad, thick and thin, enhances life so we may all hyper-thrive. Why not make things as good as they possibly can be by just communicating effortlessly?
                        </p>
                      </div>

                      <Button 
                        onClick={() => setCurrentPhase(1)}
                        className="bg-black text-white hover:bg-gray-800 px-12 py-6 text-xl"
                        style={{ borderRadius: 0 }}
                      >
                        Begin Your Journey
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Deep Dive: Complete Teaching Content */}
                <Card className="mt-8 border-4 border-black bg-gray-50" style={{ borderRadius: 0 }}>
                  <CardHeader>
                    <CardTitle className="text-2xl">📚 Deep Dive: The Science of Belonging</CardTitle>
                    <p className="text-gray-600">Complete teaching content from The Belonging Sessions</p>
                  </CardHeader>
                  <CardContent className="p-8 space-y-6">
                    
                    {/* Key Principles */}
                    <div>
                      <h3 className="text-xl font-bold mb-4">Core Principles of Compassionate Communication</h3>
                      <div className="bg-white p-6 border-l-4 border-green-600">
                        <p className="text-lg font-bold mb-3 text-green-800">
                          "The purpose of Nonviolent Communication is to Make Life More Wonderful by Caring About My Needs and the Other Person's Needs Moment by Moment!!!"
                        </p>
                        <ul className="space-y-2 text-gray-700">
                          <li>• The goal is NOT to get your way, but to create connection where everyone's needs are met</li>
                          <li>• Everything anyone ever does is motivated by a universal human need</li>
                          <li>• All human beings have the same needs - we differ in strategies</li>
                          <li>• Our feelings exist to tell us whether a need has been met or not</li>
                          <li>• The root of conflict is never conflicting needs, but conflicting strategies</li>
                        </ul>
                      </div>
                    </div>

                    {/* 5 Key Practices */}
                    <div>
                      <h3 className="text-xl font-bold mb-4">5 Key Practices for Compassionate Conversation</h3>
                      <div className="grid md:grid-cols-5 gap-4">
                        <div className="p-4 bg-blue-100 border-2 border-blue-600">
                          <h4 className="font-bold text-blue-800">1. Slow it down</h4>
                          <p className="text-sm text-blue-700">Speak at a rate the listener can really get what you're saying</p>
                        </div>
                        <div className="p-4 bg-green-100 border-2 border-green-600">
                          <h4 className="font-bold text-green-800">2. Chunk it down</h4>
                          <p className="text-sm text-green-700">Talk about smaller bits so the listener can get your meaning</p>
                        </div>
                        <div className="p-4 bg-purple-100 border-2 border-purple-600">
                          <h4 className="font-bold text-purple-800">3. Reflect back</h4>
                          <p className="text-sm text-purple-700">Say back what you heard to ensure understanding</p>
                        </div>
                        <div className="p-4 bg-orange-100 border-2 border-orange-600">
                          <h4 className="font-bold text-orange-800">4. Alternate speaking</h4>
                          <p className="text-sm text-orange-700">Go back and forth at a rate that meets everyone's needs</p>
                        </div>
                        <div className="p-4 bg-pink-100 border-2 border-pink-600">
                          <h4 className="font-bold text-pink-800">5. Express gratitude</h4>
                          <p className="text-sm text-pink-700">Say "thank you" whenever sincerely grateful</p>
                        </div>
                      </div>
                    </div>

                    {/* Deep Dive: Say What You Mean */}
                    <div className="bg-gradient-to-br from-amber-100 to-amber-50 border-4 border-amber-600 rounded-lg p-6">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg">
                            <span className="text-white text-2xl">📖</span>
                          </div>
                          <div>
                            <h3 className="text-amber-800 font-bold text-lg">Deep Dive: Say What You Mean</h3>
                            <p className="text-amber-700 text-sm">Three Steps to Effective Conversation • By Oren Jay Sofer</p>
                          </div>
                        </div>
                        <Button
                          onClick={() => window.location.href = '/workbook/say-what-you-mean'}
                          className="bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white px-6"
                        >
                          Explore Deep Dive →
                        </Button>
                      </div>
                      <div className="mt-5 grid grid-cols-3 gap-3">
                        <div className="bg-blue-100 border-2 border-blue-400 rounded-lg px-4 py-3 text-center">
                          <span className="text-blue-800 font-semibold text-sm">1. Lead with Presence</span>
                        </div>
                        <div className="bg-rose-100 border-2 border-rose-400 rounded-lg px-4 py-3 text-center">
                          <span className="text-rose-800 font-semibold text-sm">2. Curiosity & Care</span>
                        </div>
                        <div className="bg-emerald-100 border-2 border-emerald-400 rounded-lg px-4 py-3 text-center">
                          <span className="text-emerald-800 font-semibold text-sm">3. Focus on What Matters</span>
                        </div>
                      </div>
                    </div>

                    {/* Street Giraffe */}
                    <div>
                      <h3 className="text-xl font-bold mb-4">Street Giraffe - Natural Language</h3>
                      <div className="bg-white p-6 border-l-4 border-yellow-500">
                        <p className="text-base text-gray-700 mb-4">
                          The point isn't to use specific words - it's to incorporate the consciousness into how you think and communicate, especially during conflict.
                        </p>
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <h4 className="font-bold mb-2">Classical Giraffe:</h4>
                            <p className="text-sm italic">"When I hear you say you won't be home until late, I feel disappointed because I need connection. Would you be willing to have dinner together tomorrow night?"</p>
                          </div>
                          <div>
                            <h4 className="font-bold mb-2">Street Giraffe:</h4>
                            <p className="text-sm italic">"Oh darn, I'm disappointed to hear that. I was hoping for some closeness tonight. Are you interested in having dinner tomorrow night?"</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Marshall Rosenberg Wisdom */}
                    <div>
                      <h3 className="text-xl font-bold mb-4">Marshall Rosenberg's Core Questions</h3>
                      <div className="bg-white p-6 border-l-4 border-red-600">
                        <p className="text-lg font-bold text-red-800 mb-3">
                          "NVC is designed to answer two of the most asked questions on this planet:"
                        </p>
                        <div className="space-y-2">
                          <p className="text-base"><strong>1.</strong> What is alive in you? (How are you doing?)</p>
                          <p className="text-base"><strong>2.</strong> What ideas do we have to make life more wonderful?</p>
                        </div>
                      </div>
                    </div>

                  </CardContent>
                </Card>

              </div>
            )}

            {/* Phase 1: Observations (Oceanic Blue) - Complete */}
            {currentPhase === 1 && (
              <div className="space-y-8">
                <Card className="border-4 border-blue-600 bg-white" style={{ borderRadius: 0, boxShadow: '12px 12px 0px rgba(37,99,235,0.2)' }}>
                  <CardHeader className="bg-blue-50">
                    <CardTitle className="text-3xl flex items-center gap-3">
                      <Eye className="w-8 h-8 text-blue-600" />
                      O - Observations (Oceanic Blue)
                    </CardTitle>
                    <p className="text-xl text-blue-800">
                      See clearly like observing the sea. Express yourself honestly without blaming or criticizing.
                    </p>
                  </CardHeader>
                  <CardContent className="p-8">
                    
                    {/* What is an Observation? */}
                    <Card className="mb-8 border-2 border-blue-600 bg-blue-50" style={{ borderRadius: 0 }}>
                      <CardContent className="p-6">
                        <h3 className="text-xl font-bold mb-4 text-blue-800">What is an Observation?</h3>
                        <ul className="space-y-2 text-blue-800">
                          <li>• An observation is what you notice, independent of any evaluation</li>
                          <li>• An observation is what you could videotape or audio record</li>
                          <li>• Creates shared reality with the other person</li>
                          <li>• The observation gives context for expressing feelings and needs</li>
                        </ul>
                      </CardContent>
                    </Card>

                    {/* Observation vs Evaluation Practice */}
                    <div className="grid md:grid-cols-2 gap-6 mb-8">
                      <Card className="border-2 border-green-600 bg-green-50" style={{ borderRadius: 0 }}>
                        <CardHeader>
                          <CardTitle className="text-lg text-green-800">✅ Pure Observations</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 text-sm">
                            <p>"When you walked in without saying hello..."</p>
                            <p>"When I heard your voice get louder than usual..."</p>
                            <p>"When I saw the dishes still in the sink at 8pm..."</p>
                            <p>"When you said 'I don't care'..."</p>
                            <p>"When I noticed you looking at your phone during our conversation..."</p>
                          </div>
                        </CardContent>
                      </Card>
                      
                      <Card className="border-2 border-red-600 bg-red-50" style={{ borderRadius: 0 }}>
                        <CardHeader>
                          <CardTitle className="text-lg text-red-800">❌ Evaluations (Avoid)</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 text-sm">
                            <p>"When you were rude..."</p>
                            <p>"When you got angry at me..."</p>
                            <p>"When you were being lazy..."</p>
                            <p>"When you didn't care..."</p>
                            <p>"When you ignored me..."</p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Interactive Practice */}
                    <Card className="border-2 border-black bg-white" style={{ borderRadius: 0 }}>
                      <CardHeader>
                        <CardTitle className="text-xl">Practice: Transform Evaluations to Observations</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <label className="block font-bold mb-2">Write an evaluation you often make:</label>
                          <Input
                            placeholder="e.g., 'They are so selfish' or 'You never listen'"
                            className="border-red-600"
                            style={{ borderRadius: 0 }}
                          />
                        </div>
                        <div>
                          <label className="block font-bold mb-2">Transform it to a pure observation:</label>
                          <Textarea
                            placeholder="What could a video camera record? What did you actually see or hear?"
                            className="border-blue-600 min-h-[80px]"
                            style={{ borderRadius: 0 }}
                          />
                        </div>
                        <Button
                          className="bg-blue-600 text-white hover:bg-blue-700 w-full py-3"
                          style={{ borderRadius: 0 }}
                          onClick={() => {
                            const userId = localStorage.getItem('workbook_user_id');
                            if (userId) {
                              toast.success('Observation practice saved!');
                            } else {
                              toast.info('Create an account to save your practice!');
                            }
                          }}
                        >
                          💾 Save Observation Practice
                        </Button>
                      </CardContent>
                    </Card>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Phase 2: F - Feelings (Red) - MEGA LIST */}
            {currentPhase === 2 && (
              <div className="space-y-8">
                <Card className="border-4 border-red-600 bg-white" style={{ borderRadius: 0, boxShadow: '12px 12px 0px rgba(220,38,38,0.2)' }}>
                  <CardHeader className="bg-red-50">
                    <CardTitle className="text-3xl flex items-center gap-3">
                      <Heart className="w-8 h-8 text-red-600" />
                      F - Feelings: The Complete Collection
                    </CardTitle>
                    <p className="text-xl text-red-800">
                      Feelings, body sensations, and what's alive in you — these are basically all the same thing!
                    </p>
                    <p className="text-lg text-red-700">
                      Your internal sense of what is happening to you. Click words to hear their unique tone!
                    </p>
                  </CardHeader>
                  <CardContent className="p-8">
                    
                    {/* Scenario Practice */}
                    <div className="mb-8 p-6 bg-red-50 border-l-4 border-red-600">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-xl font-bold">Feel Into This Scenario:</h3>
                        <Button onClick={generateScenario} variant="outline" className="border-red-600">
                          <RotateCcw className="w-4 h-4 mr-2" />
                          New Scenario
                        </Button>
                      </div>
                      <p className="text-lg italic text-red-800 mb-4">
                        "{scenario}"
                      </p>
                      <p className="text-base text-gray-700">
                        What feelings, sensations, or aliveness do you notice? Click words below that resonate!
                      </p>
                    </div>

                    {/* Body Sensations from Page 7 */}
                    <div className="mb-8">
                      <h3 className="text-2xl font-bold mb-4 text-red-800">🫀 Body Sensations (Page 7)</h3>
                      <p className="text-base text-red-700 mb-4">Physical feelings that accompany emotions - aching, airy, antsy, bloated...</p>
                      <div className="grid grid-cols-4 md:grid-cols-6 gap-2 p-4 bg-red-25 border-2 border-red-400 rounded max-h-64 overflow-y-auto">
                        {ofnrData.bodySensations.map((sensation) => (
                          <Button
                            key={sensation}
                            onClick={() => {
                              if (selectedFeelings.includes(sensation)) {
                                setSelectedFeelings(prev => prev.filter(f => f !== sensation));
                              } else {
                                setSelectedFeelings(prev => [...prev, sensation]);
                              }
                              // Body sensation tone (lower C note)
                              const audio = new Audio('data:audio/wav;base64,UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAAyMjIyMjIyMjIyMjIyMjIyMjI=');
                              audio.volume = 0.2;
                              audio.play().catch(() => {});
                            }}
                            variant={selectedFeelings.includes(sensation) ? "default" : "outline"}
                            className={`text-xs py-1 ${
                              selectedFeelings.includes(sensation)
                                ? 'bg-red-400 text-white border-red-400'
                                : 'bg-white text-red-500 hover:bg-red-50 border-red-200'
                            }`}
                            style={{ borderRadius: 0 }}
                          >
                            {sensation}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* When Needs Are Satisfied */}
                    <div className="mb-8">
                      <h3 className="text-2xl font-bold mb-4 text-red-800">😊 When Needs Are SATISFIED</h3>
                      <div className="space-y-4">
                        {Object.entries(ofnrData.feelingsWhenSatisfied).map(([category, feelings]) => (
                          <div key={category}>
                            <h4 className="font-bold text-red-700 mb-2">{category}</h4>
                            <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                              {feelings.map((feeling) => (
                                <Button
                                  key={feeling}
                                  onClick={() => {
                                    if (selectedFeelings.includes(feeling)) {
                                      setSelectedFeelings(prev => prev.filter(f => f !== feeling));
                                    } else {
                                      setSelectedFeelings(prev => [...prev, feeling]);
                                    }
                                    // Happy feeling tone (higher D note)
                                    const audio = new Audio('data:audio/wav;base64,UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAA4ODg4ODg4ODg4ODg4ODg4ODg4');
                                    audio.volume = 0.3;
                                    audio.play().catch(() => {});
                                  }}
                                  variant={selectedFeelings.includes(feeling) ? "default" : "outline"}
                                  className={`text-xs py-1 ${
                                    selectedFeelings.includes(feeling)
                                      ? 'bg-red-500 text-white border-red-500'
                                      : 'bg-white text-red-600 hover:bg-red-100 border-red-300'
                                  }`}
                                  style={{ borderRadius: 0 }}
                                >
                                  {feeling}
                                </Button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* When Needs Are NOT Satisfied */}
                    <div className="mb-8">
                      <h3 className="text-2xl font-bold mb-4 text-red-800">😔 When Needs Are NOT SATISFIED</h3>
                      <div className="space-y-4 max-h-96 overflow-y-auto">
                        {Object.entries(ofnrData.feelingsWhenNotSatisfied).map(([category, feelings]) => (
                          <div key={category}>
                            <h4 className="font-bold text-red-900 mb-2">{category}</h4>
                            <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                              {feelings.map((feeling) => (
                                <Button
                                  key={feeling}
                                  onClick={() => {
                                    if (selectedFeelings.includes(feeling)) {
                                      setSelectedFeelings(prev => prev.filter(f => f !== feeling));
                                    } else {
                                      setSelectedFeelings(prev => [...prev, feeling]);
                                    }
                                    // Difficult feeling tone (lower F note)
                                    const audio = new Audio('data:audio/wav;base64,UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAAuLi4uLi4uLi4uLi4uLi4uLi4u');
                                    audio.volume = 0.25;
                                    audio.play().catch(() => {});
                                  }}
                                  variant={selectedFeelings.includes(feeling) ? "default" : "outline"}
                                  className={`text-xs py-1 ${
                                    selectedFeelings.includes(feeling)
                                      ? 'bg-red-700 text-white border-red-700'
                                      : 'bg-white text-red-700 hover:bg-red-50 border-red-500'
                                  }`}
                                  style={{ borderRadius: 0 }}
                                >
                                  {feeling}
                                </Button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Selected Feelings Display */}
                    {selectedFeelings.length > 0 && (
                      <Card className="bg-white border-4 border-black" style={{ borderRadius: 0 }}>
                        <CardContent className="p-6">
                          <h4 className="text-xl font-bold mb-4">What's Alive in You Right Now:</h4>
                          <div className="flex flex-wrap gap-2 mb-4">
                            {selectedFeelings.map((feeling) => (
                              <Badge key={feeling} className="bg-red-600 text-white text-base px-3 py-2">
                                {feeling}
                              </Badge>
                            ))}
                          </div>
                          <div className="text-center">
                            <Button
                              className="bg-red-600 text-white hover:bg-red-700"
                              style={{ borderRadius: 0 }}
                              onClick={() => {
                                const userId = localStorage.getItem('workbook_user_id');
                                if (userId) {
                                  toast.success('Feelings practice saved to your journal!');
                                } else {
                                  toast.info('Create an account to save your feelings awareness!');
                                }
                              }}
                            >
                              💾 Save This Feelings Awareness
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Steve's Wisdom */}
                    <Card className="mt-8 border-2 border-black bg-white" style={{ borderRadius: 0 }}>
                      <CardContent className="p-6 text-center">
                        <p className="text-lg italic text-gray-800">
                          "This is not about being a master of compassionate communication. 
                          This is about mastering compassionate communication so you can be most alive and connected, 
                          maximizing the well-being of yourself and the people you engage with."
                        </p>
                        <p className="text-base text-gray-600 mt-3">— Steve Torma</p>
                      </CardContent>
                    </Card>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Phase 3: Basic Feelings & Needs Exercise (Page 15) */}
            {currentPhase === 3 && (
              <div className="space-y-8">
                
                {/* EPIC Nine Basic Human Needs Infographic - Compact Version */}
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-900 via-green-800 to-teal-900 p-4 md:p-6 shadow-xl" data-testid="nine-needs-infographic">
                  {/* Animated background patterns */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-48 h-48 bg-white rounded-full filter blur-3xl animate-pulse" style={{ animationDuration: '4s' }}></div>
                    <div className="absolute bottom-0 right-0 w-40 h-40 bg-emerald-300 rounded-full filter blur-3xl animate-pulse" style={{ animationDuration: '6s', animationDelay: '2s' }}></div>
                    <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-teal-400 rounded-full filter blur-3xl animate-pulse" style={{ animationDuration: '5s', animationDelay: '1s' }}></div>
                  </div>
                  
                  {/* Header */}
                  <div className="relative text-center mb-5">
                    <div className="inline-block">
                      <span className="text-3xl mb-2 block">🌳</span>
                      <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 tracking-tight">
                        The Nine Basic Human Needs
                      </h2>
                      <p className="text-emerald-200 text-sm md:text-base max-w-xl mx-auto">
                        Universal needs shared by all humans — the foundation of compassionate connection
                      </p>
                    </div>
                  </div>
                  
                  {/* The Nine Needs Grid */}
                  <div className="relative grid grid-cols-1 md:grid-cols-3 gap-3 max-w-4xl mx-auto">
                    
                    {/* 1. Sustenance */}
                    <div className="group relative bg-gradient-to-br from-amber-500/20 to-orange-600/20 backdrop-blur-sm border border-amber-400/50 rounded-xl p-3 transform transition-all duration-500 hover:scale-105 hover:shadow-xl hover:shadow-amber-500/20 cursor-pointer">
                      <div className="absolute -top-2 -left-2 w-6 h-6 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center text-xs font-bold text-white shadow-md transform group-hover:rotate-12 transition-transform">1</div>
                      <div className="text-2xl mb-2 transform group-hover:scale-110 transition-transform">🍞</div>
                      <h3 className="text-base font-bold text-amber-200 mb-1">Sustenance</h3>
                      <p className="text-amber-100/80 text-xs leading-relaxed">Food, water, air, shelter — physical foundations</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        <span className="px-1.5 py-0.5 bg-amber-500/30 rounded-full text-[10px] text-amber-200">nourishment</span>
                        <span className="px-1.5 py-0.5 bg-amber-500/30 rounded-full text-[10px] text-amber-200">health</span>
                      </div>
                    </div>
                    
                    {/* 2. Safety */}
                    <div className="group relative bg-gradient-to-br from-blue-500/20 to-indigo-600/20 backdrop-blur-sm border border-blue-400/50 rounded-xl p-3 transform transition-all duration-500 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/20 cursor-pointer">
                      <div className="absolute -top-2 -left-2 w-6 h-6 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-lg flex items-center justify-center text-xs font-bold text-white shadow-md transform group-hover:rotate-12 transition-transform">2</div>
                      <div className="text-2xl mb-2 transform group-hover:scale-110 transition-transform">🛡️</div>
                      <h3 className="text-base font-bold text-blue-200 mb-1">Safety</h3>
                      <p className="text-blue-100/80 text-xs leading-relaxed">Protection, security, stability</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        <span className="px-1.5 py-0.5 bg-blue-500/30 rounded-full text-[10px] text-blue-200">security</span>
                        <span className="px-1.5 py-0.5 bg-blue-500/30 rounded-full text-[10px] text-blue-200">trust</span>
                      </div>
                    </div>
                    
                    {/* 3. Love */}
                    <div className="group relative bg-gradient-to-br from-rose-500/20 to-pink-600/20 backdrop-blur-sm border border-rose-400/50 rounded-xl p-3 transform transition-all duration-500 hover:scale-105 hover:shadow-xl hover:shadow-rose-500/20 cursor-pointer">
                      <div className="absolute -top-2 -left-2 w-6 h-6 bg-gradient-to-br from-rose-400 to-pink-500 rounded-lg flex items-center justify-center text-xs font-bold text-white shadow-md transform group-hover:rotate-12 transition-transform">3</div>
                      <div className="text-2xl mb-2 transform group-hover:scale-110 transition-transform">💗</div>
                      <h3 className="text-base font-bold text-rose-200 mb-1">Love</h3>
                      <p className="text-rose-100/80 text-xs leading-relaxed">Affection, intimacy, care</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        <span className="px-1.5 py-0.5 bg-rose-500/30 rounded-full text-[10px] text-rose-200">connection</span>
                        <span className="px-1.5 py-0.5 bg-rose-500/30 rounded-full text-[10px] text-rose-200">intimacy</span>
                      </div>
                    </div>
                    
                    {/* 4. Empathy */}
                    <div className="group relative bg-gradient-to-br from-purple-500/20 to-violet-600/20 backdrop-blur-sm border border-purple-400/50 rounded-xl p-3 transform transition-all duration-500 hover:scale-105 hover:shadow-xl hover:shadow-purple-500/20 cursor-pointer">
                      <div className="absolute -top-2 -left-2 w-6 h-6 bg-gradient-to-br from-purple-400 to-violet-500 rounded-lg flex items-center justify-center text-xs font-bold text-white shadow-md transform group-hover:rotate-12 transition-transform">4</div>
                      <div className="text-2xl mb-2 transform group-hover:scale-110 transition-transform">🤝</div>
                      <h3 className="text-base font-bold text-purple-200 mb-1">Empathy</h3>
                      <p className="text-purple-100/80 text-xs leading-relaxed">Understanding, being heard, respect</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        <span className="px-1.5 py-0.5 bg-purple-500/30 rounded-full text-[10px] text-purple-200">understanding</span>
                        <span className="px-1.5 py-0.5 bg-purple-500/30 rounded-full text-[10px] text-purple-200">heard</span>
                      </div>
                    </div>
                    
                    {/* 5. Rest, Recreation & Play */}
                    <div className="group relative bg-gradient-to-br from-cyan-500/20 to-sky-600/20 backdrop-blur-sm border border-cyan-400/50 rounded-xl p-3 transform transition-all duration-500 hover:scale-105 hover:shadow-xl hover:shadow-cyan-500/20 cursor-pointer">
                      <div className="absolute -top-2 -left-2 w-6 h-6 bg-gradient-to-br from-cyan-400 to-sky-500 rounded-lg flex items-center justify-center text-xs font-bold text-white shadow-md transform group-hover:rotate-12 transition-transform">5</div>
                      <div className="text-2xl mb-2 transform group-hover:scale-110 transition-transform">🎮</div>
                      <h3 className="text-base font-bold text-cyan-200 mb-1">Rest & Play</h3>
                      <p className="text-cyan-100/80 text-xs leading-relaxed">Relaxation, fun, rejuvenation</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        <span className="px-1.5 py-0.5 bg-cyan-500/30 rounded-full text-[10px] text-cyan-200">relaxation</span>
                        <span className="px-1.5 py-0.5 bg-cyan-500/30 rounded-full text-[10px] text-cyan-200">fun</span>
                      </div>
                    </div>
                    
                    {/* 6. Community */}
                    <div className="group relative bg-gradient-to-br from-green-500/20 to-emerald-600/20 backdrop-blur-sm border border-green-400/50 rounded-xl p-3 transform transition-all duration-500 hover:scale-105 hover:shadow-xl hover:shadow-green-500/20 cursor-pointer">
                      <div className="absolute -top-2 -left-2 w-6 h-6 bg-gradient-to-br from-green-400 to-emerald-500 rounded-lg flex items-center justify-center text-xs font-bold text-white shadow-md transform group-hover:rotate-12 transition-transform">6</div>
                      <div className="text-2xl mb-2 transform group-hover:scale-110 transition-transform">🏘️</div>
                      <h3 className="text-base font-bold text-green-200 mb-1">Community</h3>
                      <p className="text-green-100/80 text-xs leading-relaxed">Belonging, fellowship, contribution</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        <span className="px-1.5 py-0.5 bg-green-500/30 rounded-full text-[10px] text-green-200">belonging</span>
                        <span className="px-1.5 py-0.5 bg-green-500/30 rounded-full text-[10px] text-green-200">support</span>
                      </div>
                    </div>
                    
                    {/* 7. Creativity */}
                    <div className="group relative bg-gradient-to-br from-yellow-500/20 to-amber-600/20 backdrop-blur-sm border border-yellow-400/50 rounded-xl p-3 transform transition-all duration-500 hover:scale-105 hover:shadow-xl hover:shadow-yellow-500/20 cursor-pointer">
                      <div className="absolute -top-2 -left-2 w-6 h-6 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-lg flex items-center justify-center text-xs font-bold text-white shadow-md transform group-hover:rotate-12 transition-transform">7</div>
                      <div className="text-2xl mb-2 transform group-hover:scale-110 transition-transform">🎨</div>
                      <h3 className="text-base font-bold text-yellow-200 mb-1">Creativity</h3>
                      <p className="text-yellow-100/80 text-xs leading-relaxed">Expression, growth, learning</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        <span className="px-1.5 py-0.5 bg-yellow-500/30 rounded-full text-[10px] text-yellow-200">expression</span>
                        <span className="px-1.5 py-0.5 bg-yellow-500/30 rounded-full text-[10px] text-yellow-200">growth</span>
                      </div>
                    </div>
                    
                    {/* 8. Autonomy */}
                    <div className="group relative bg-gradient-to-br from-teal-500/20 to-green-600/20 backdrop-blur-sm border border-teal-400/50 rounded-xl p-3 transform transition-all duration-500 hover:scale-105 hover:shadow-xl hover:shadow-teal-500/20 cursor-pointer">
                      <div className="absolute -top-2 -left-2 w-6 h-6 bg-gradient-to-br from-teal-400 to-green-500 rounded-lg flex items-center justify-center text-xs font-bold text-white shadow-md transform group-hover:rotate-12 transition-transform">8</div>
                      <div className="text-2xl mb-2 transform group-hover:scale-110 transition-transform">🦅</div>
                      <h3 className="text-base font-bold text-teal-200 mb-1">Autonomy</h3>
                      <p className="text-teal-100/80 text-xs leading-relaxed">Freedom, choice, independence</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        <span className="px-1.5 py-0.5 bg-teal-500/30 rounded-full text-[10px] text-teal-200">freedom</span>
                        <span className="px-1.5 py-0.5 bg-teal-500/30 rounded-full text-[10px] text-teal-200">choice</span>
                      </div>
                    </div>
                    
                    {/* 9. Meaning / Purpose */}
                    <div className="group relative bg-gradient-to-br from-fuchsia-500/20 to-purple-600/20 backdrop-blur-sm border border-fuchsia-400/50 rounded-xl p-3 transform transition-all duration-500 hover:scale-105 hover:shadow-xl hover:shadow-fuchsia-500/20 cursor-pointer">
                      <div className="absolute -top-2 -left-2 w-6 h-6 bg-gradient-to-br from-fuchsia-400 to-purple-500 rounded-lg flex items-center justify-center text-xs font-bold text-white shadow-md transform group-hover:rotate-12 transition-transform">9</div>
                      <div className="text-2xl mb-2 transform group-hover:scale-110 transition-transform">🎯</div>
                      <h3 className="text-base font-bold text-fuchsia-200 mb-1">Meaning & Purpose</h3>
                      <p className="text-fuchsia-100/80 text-xs leading-relaxed">Purpose, contribution, legacy</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        <span className="px-1.5 py-0.5 bg-fuchsia-500/30 rounded-full text-[10px] text-fuchsia-200">purpose</span>
                        <span className="px-1.5 py-0.5 bg-fuchsia-500/30 rounded-full text-[10px] text-fuchsia-200">legacy</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Bottom wisdom quote */}
                  <div className="relative mt-5 text-center">
                    <div className="inline-block bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 max-w-xl">
                      <p className="text-emerald-100 text-sm italic leading-relaxed">
                        "When any of these nine basic needs are unmet, we experience difficult feelings. When they are met, we flourish."
                      </p>
                      <p className="text-emerald-300 mt-2 font-semibold text-xs">— The Belonging Sessions</p>
                    </div>
                  </div>
                </div>

                <Card className="border-4 border-green-600 bg-white" style={{ borderRadius: 0, boxShadow: '12px 12px 0px rgba(34,197,94,0.2)' }}>
                  <CardHeader className="bg-green-50">
                    <CardTitle className="text-3xl flex items-center gap-3">
                      <Leaf className="w-8 h-8 text-green-600" />
                      Basic Feelings & Needs Exercise
                    </CardTitle>
                    <p className="text-lg text-green-800">
                      According to Marshall Rosenberg: "The purpose of Nonviolent Communication is to create a quality of connection where everyone's needs are met through compassionate giving."
                    </p>
                  </CardHeader>
                  <CardContent className="p-8">
                    
                    <div className="mb-8">
                      <h3 className="text-xl font-bold mb-4">Step 1: Choose a Need</h3>
                      <p className="text-base text-gray-700 mb-4">
                        Select from the nine basic human needs above, or explore the detailed categories below.
                      </p>
                      
                      {/* All Needs from Page 5 - Organized by Category */}
                      <div className="space-y-6">
                        <div>
                          <h4 className="font-bold text-green-800 mb-3">🌱 Sustenance</h4>
                          <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
                            {ofnrData.needsByCategory.Sustenance.map((need) => (
                              <Button
                                key={need}
                                onClick={() => setSelectedNeeds([need])}
                                variant={selectedNeeds.includes(need) ? "default" : "outline"}
                                className={`border-2 border-green-600 text-xs transition-all hover:scale-110 hover:shadow-lg ${
                                  selectedNeeds.includes(need) 
                                    ? 'bg-green-600 text-white' 
                                    : 'bg-white text-green-600 hover:bg-green-600 hover:text-white'
                                }`}
                                style={{ borderRadius: 0 }}
                              >
                                {need}
                              </Button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <h4 className="font-bold text-green-800 mb-3">🛡️ Safety</h4>
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                            {ofnrData.needsByCategory.Safety.map((need) => (
                              <Button
                                key={need}
                                onClick={() => setSelectedNeeds([need])}
                                variant={selectedNeeds.includes(need) ? "default" : "outline"}
                                className={`border-2 border-green-600 text-xs transition-all hover:scale-110 hover:shadow-lg ${
                                  selectedNeeds.includes(need) 
                                    ? 'bg-green-600 text-white' 
                                    : 'bg-white text-green-600 hover:bg-green-600 hover:text-white'
                                }`}
                                style={{ borderRadius: 0 }}
                              >
                                {need}
                              </Button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <h4 className="font-bold text-green-800 mb-3">💝 Love</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {ofnrData.needsByCategory.Love.map((need) => (
                              <Button
                                key={need}
                                onClick={() => setSelectedNeeds([need])}
                                variant={selectedNeeds.includes(need) ? "default" : "outline"}
                                className={`border-2 border-green-600 text-xs transition-all hover:scale-110 hover:shadow-lg ${
                                  selectedNeeds.includes(need) 
                                    ? 'bg-green-600 text-white' 
                                    : 'bg-white text-green-600 hover:bg-green-600 hover:text-white'
                                }`}
                                style={{ borderRadius: 0 }}
                              >
                                {need}
                              </Button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <h4 className="font-bold text-green-800 mb-3">🤝 Empathy</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {ofnrData.needsByCategory.Empathy.map((need) => (
                              <Button
                                key={need}
                                onClick={() => setSelectedNeeds([need])}
                                variant={selectedNeeds.includes(need) ? "default" : "outline"}
                                className={`border-2 border-green-600 text-xs transition-all hover:scale-110 hover:shadow-lg ${
                                  selectedNeeds.includes(need) 
                                    ? 'bg-green-600 text-white' 
                                    : 'bg-white text-green-600 hover:bg-green-600 hover:text-white'
                                }`}
                                style={{ borderRadius: 0 }}
                              >
                                {need}
                              </Button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <h4 className="font-bold text-green-800 mb-3">🎮 Rest, Recreation & Play</h4>
                          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                            {ofnrData.needsByCategory["Rest Recreation & Play"].map((need) => (
                              <Button
                                key={need}
                                onClick={() => setSelectedNeeds([need])}
                                variant={selectedNeeds.includes(need) ? "default" : "outline"}
                                className={`border-2 border-green-600 text-xs transition-all hover:scale-110 hover:shadow-lg ${
                                  selectedNeeds.includes(need) 
                                    ? 'bg-green-600 text-white' 
                                    : 'bg-white text-green-600 hover:bg-green-600 hover:text-white'
                                }`}
                                style={{ borderRadius: 0 }}
                              >
                                {need}
                              </Button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <h4 className="font-bold text-green-800 mb-3">🏘️ Community</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {ofnrData.needsByCategory.Community.map((need) => (
                              <Button
                                key={need}
                                onClick={() => setSelectedNeeds([need])}
                                variant={selectedNeeds.includes(need) ? "default" : "outline"}
                                className={`border-2 border-green-600 text-xs transition-all hover:scale-110 hover:shadow-lg ${
                                  selectedNeeds.includes(need) 
                                    ? 'bg-green-600 text-white' 
                                    : 'bg-white text-green-600 hover:bg-green-600 hover:text-white'
                                }`}
                                style={{ borderRadius: 0 }}
                              >
                                {need}
                              </Button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <h4 className="font-bold text-green-800 mb-3">🎨 Creativity</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {ofnrData.needsByCategory.Creativity.slice(0, 20).map((need) => (
                              <Button
                                key={need}
                                onClick={() => setSelectedNeeds([need])}
                                variant={selectedNeeds.includes(need) ? "default" : "outline"}
                                className={`border-2 border-green-600 text-xs transition-all hover:scale-110 hover:shadow-lg ${
                                  selectedNeeds.includes(need) 
                                    ? 'bg-green-600 text-white' 
                                    : 'bg-white text-green-600 hover:bg-green-600 hover:text-white'
                                }`}
                                style={{ borderRadius: 0 }}
                              >
                                {need}
                              </Button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <h4 className="font-bold text-green-800 mb-3">🦅 Autonomy</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {ofnrData.needsByCategory.Autonomy.map((need) => (
                              <Button
                                key={need}
                                onClick={() => setSelectedNeeds([need])}
                                variant={selectedNeeds.includes(need) ? "default" : "outline"}
                                className={`border-2 border-green-600 text-xs transition-all hover:scale-110 hover:shadow-lg ${
                                  selectedNeeds.includes(need) 
                                    ? 'bg-green-600 text-white' 
                                    : 'bg-white text-green-600 hover:bg-green-600 hover:text-white'
                                }`}
                                style={{ borderRadius: 0 }}
                              >
                                {need}
                              </Button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <h4 className="font-bold text-green-800 mb-3">🎯 Meaning or Purpose in Life</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {ofnrData.needsByCategory["Meaning or Purpose in Life"].slice(0, 20).map((need) => (
                              <Button
                                key={need}
                                onClick={() => setSelectedNeeds([need])}
                                variant={selectedNeeds.includes(need) ? "default" : "outline"}
                                className={`border-2 border-green-600 text-xs transition-all hover:scale-110 hover:shadow-lg ${
                                  selectedNeeds.includes(need) 
                                    ? 'bg-green-600 text-white' 
                                    : 'bg-white text-green-600 hover:bg-green-600 hover:text-white'
                                }`}
                                style={{ borderRadius: 0 }}
                              >
                                {need}
                              </Button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {selectedNeeds.length > 0 && (
                      <div className="space-y-8">
                        <div className="grid md:grid-cols-2 gap-8">
                          {/* When Need is Satisfied - ALL CATEGORIES FROM PAGE 6 */}
                          <Card className="border-4 border-red-500 bg-red-50" style={{ borderRadius: 0 }}>
                            <CardHeader>
                              <CardTitle className="text-xl text-red-800">
                                🟥 When {selectedNeeds[0]} is SATISFIED
                              </CardTitle>
                              <p className="text-sm text-red-700">All feelings from page 6 - when needs are satisfied</p>
                            </CardHeader>
                            <CardContent className="max-h-96 overflow-y-auto">
                              {/* ALL 11 CATEGORIES FROM PAGE 6 */}
                              {Object.entries(ofnrData.feelingsWhenSatisfied).map(([category, feelings]) => (
                                <div key={category} className="mb-4">
                                  <h5 className="font-bold text-red-800 mb-2">{category}</h5>
                                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                                    {feelings.map((feeling) => (
                                      <Button
                                        key={feeling}
                                        onClick={() => {
                                          if (selectedSatisfiedFeelings.includes(feeling)) {
                                            setSelectedSatisfiedFeelings(prev => prev.filter(f => f !== feeling));
                                          } else {
                                            setSelectedSatisfiedFeelings(prev => [...prev, feeling]);
                                          }
                                        }}
                                        variant={selectedSatisfiedFeelings.includes(feeling) ? "default" : "outline"}
                                        className={`text-xs py-1 transition-all hover:scale-110 hover:shadow-lg ${
                                          selectedSatisfiedFeelings.includes(feeling)
                                            ? 'bg-red-500 text-white border-red-500 shadow-lg'
                                            : 'bg-white text-red-600 hover:bg-red-500 hover:text-white border-red-300'
                                        }`}
                                        style={{ borderRadius: 0 }}
                                      >
                                        {feeling}
                                      </Button>
                                    ))}
                                  </div>
                                </div>
                              ))}
                              
                              {/* Body Sensations from Page 7 */}
                              <div className="mt-6 p-4 bg-red-25 border-2 border-red-400 rounded">
                                <h5 className="font-bold text-red-800 mb-3">🫀 Body Sensations (Page 7)</h5>
                                <p className="text-sm text-red-700 mb-3">Physical sensations that accompany feelings</p>
                                <div className="grid grid-cols-3 md:grid-cols-4 gap-1 max-h-32 overflow-y-auto">
                                  {ofnrData.bodySensations.map((sensation) => (
                                    <Button
                                      key={sensation}
                                      onClick={() => {
                                        if (selectedSatisfiedFeelings.includes(sensation)) {
                                          setSelectedSatisfiedFeelings(prev => prev.filter(f => f !== sensation));
                                        } else {
                                          setSelectedSatisfiedFeelings(prev => [...prev, sensation]);
                                        }
                                      }}
                                      variant={selectedSatisfiedFeelings.includes(sensation) ? "default" : "outline"}
                                      className={`text-xs py-1 ${
                                        selectedSatisfiedFeelings.includes(sensation)
                                          ? 'bg-red-400 text-white border-red-400'
                                          : 'bg-white text-red-500 hover:bg-red-50 border-red-200'
                                      }`}
                                      style={{ borderRadius: 0 }}
                                    >
                                      {sensation}
                                    </Button>
                                  ))}
                                </div>
                              </div>

                              <Textarea
                                placeholder="Or type your own feelings when this need is satisfied..."
                                className="mt-4 border-red-500"
                                style={{ borderRadius: 0 }}
                              />
                            </CardContent>
                          </Card>

                          {/* When Need is NOT Satisfied - ALL CATEGORIES FROM PAGE 6 */}
                          <Card className="border-4 border-red-700 bg-red-100" style={{ borderRadius: 0 }}>
                            <CardHeader>
                              <CardTitle className="text-xl text-red-900">
                                🟥 When {selectedNeeds[0]} is NOT SATISFIED
                              </CardTitle>
                              <p className="text-sm text-red-800">All feelings from page 6 - when needs are not satisfied</p>
                            </CardHeader>
                            <CardContent className="max-h-96 overflow-y-auto">
                              {/* ALL 14 CATEGORIES FROM PAGE 6 */}
                              {Object.entries(ofnrData.feelingsWhenNotSatisfied).map(([category, feelings]) => (
                                <div key={category} className="mb-4">
                                  <h5 className="font-bold text-red-900 mb-2">{category}</h5>
                                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                                    {feelings.map((feeling) => (
                                      <Button
                                        key={feeling}
                                        onClick={() => {
                                          if (selectedUnsatisfiedFeelings.includes(feeling)) {
                                            setSelectedUnsatisfiedFeelings(prev => prev.filter(f => f !== feeling));
                                          } else {
                                            setSelectedUnsatisfiedFeelings(prev => [...prev, feeling]);
                                          }
                                        }}
                                        variant={selectedUnsatisfiedFeelings.includes(feeling) ? "default" : "outline"}
                                        className={`text-xs py-1 ${
                                          selectedUnsatisfiedFeelings.includes(feeling)
                                            ? 'bg-red-700 text-white border-red-700'
                                            : 'bg-white text-red-700 hover:bg-red-700 hover:text-white hover:scale-110 hover:shadow-lg border-red-500'
                                        }`}
                                        style={{ borderRadius: 0 }}
                                      >
                                        {feeling}
                                      </Button>
                                    ))}
                                  </div>
                                </div>
                              ))}
                              <Textarea
                                placeholder="Or type your own feelings when this need is not satisfied..."
                                className="mt-4 border-red-700"
                                style={{ borderRadius: 0 }}
                              />
                            </CardContent>
                          </Card>
                        </div>

                        <Card className="border-2 border-black bg-white" style={{ borderRadius: 0 }}>
                          <CardContent className="p-6">
                            <div className="space-y-4">
                              <div>
                                <p className="text-lg font-bold mb-2">
                                  When <span className="text-green-600">{selectedNeeds[0]}</span> is satisfied, you selected:
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {selectedSatisfiedFeelings.map((feeling) => (
                                    <Badge key={feeling} className="bg-red-500 text-white px-3 py-1">
                                      {feeling}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                              
                              <div>
                                <p className="text-lg font-bold mb-2">
                                  When <span className="text-green-600">{selectedNeeds[0]}</span> is NOT satisfied, you selected:
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {selectedUnsatisfiedFeelings.map((feeling) => (
                                    <Badge key={feeling} className="bg-red-700 text-white px-3 py-1">
                                      {feeling}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            </div>

                            <div className="mt-6 text-center">
                              <p className="text-base text-gray-700 mb-4">
                                <strong>Practice:</strong> Notice how your feelings change based on whether your needs are met.
                              </p>
                              <div className="flex gap-4 justify-center">
                                <Button
                                  className="bg-black text-white hover:bg-gray-800"
                                  style={{ borderRadius: 0 }}
                                  onClick={() => {
                                    const userId = localStorage.getItem('workbook_user_id');
                                    if (userId) {
                                      toast.success(`Exercise saved to your journal!`);
                                    } else {
                                      toast.info(`Create an account to save your practice!`);
                                    }
                                  }}
                                >
                                  💾 Save This Practice
                                </Button>
                                <Button
                                  className="bg-orange-600 text-white hover:bg-orange-700"
                                  style={{ borderRadius: 0 }}
                                  onClick={() => {
                                    // Reset exercise with tone
                                    setSelectedNeeds([]);
                                    setSelectedSatisfiedFeelings([]);
                                    setSelectedUnsatisfiedFeelings([]);
                                    
                                    // Play reset tone
                                    const audio = new Audio('data:audio/wav;base64,UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAAxMjM0NTY3ODkwOTg3NjU0MzIx');
                                    audio.volume = 0.4;
                                    audio.play().catch(() => {});
                                    
                                    toast.success('Exercise reset! Ready for next practice. 🔄');
                                  }}
                                >
                                  🔄 Reset Exercise
                                </Button>
                                <Button
                                  variant="outline"
                                  className="border-black"
                                  style={{ borderRadius: 0 }}
                                  onClick={() => {
                                    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
                                  }}
                                >
                                  📧 Contact Us
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}


            {/* Phase 4: Requests (Gold) */}
            {currentPhase === 4 && (
              <div className="space-y-8">
                <Card className="border-4 border-yellow-600 bg-white" style={{ borderRadius: 0, boxShadow: '12px 12px 0px rgba(234,179,8,0.2)' }}>
                  <CardHeader className="bg-yellow-50">
                    <CardTitle className="text-3xl flex items-center gap-3">
                      <Target className="w-8 h-8 text-yellow-600" />
                      R - Requests (Finding Common Ground)
                    </CardTitle>
                    <p className="text-xl text-yellow-800">
                      Gold - Requesting of each other until you find common ground, even if it's just a tiny nugget.
                    </p>
                  </CardHeader>
                  <CardContent className="p-8">
                    
                    <div className="mb-8 p-6 bg-yellow-50 border-l-4 border-yellow-600">
                      <h3 className="text-xl font-bold mb-4">What Makes a Good Request?</h3>
                      <ul className="space-y-2 text-yellow-800">
                        <li>• Specific and actionable - what exactly do you want?</li>
                        <li>• Positive language - what you DO want, not what you don't</li>
                        <li>• Present moment - what can happen now or soon</li>
                        <li>• Open to negotiation - willing to find win-win solutions</li>
                      </ul>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="p-4 bg-white border-2 border-yellow-600">
                        <h4 className="font-bold text-yellow-800 mb-3">✅ Clear Requests:</h4>
                        <div className="space-y-1 text-sm">
                          <p>"Would you be willing to listen for 5 minutes?"</p>
                          <p>"Could we schedule time to talk about this?"</p>
                          <p>"Would you tell me what you heard me say?"</p>
                        </div>
                      </div>
                      <div className="p-4 bg-red-50 border-2 border-red-600">
                        <h4 className="font-bold text-red-800 mb-3">❌ Demands (Avoid):</h4>
                        <div className="space-y-1 text-sm">
                          <p>"You need to listen to me!"</p>
                          <p>"Stop being so difficult!"</p>
                          <p>"You should understand by now!"</p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-8">
                      <h3 className="text-xl font-bold mb-4">Practice: Craft a Request</h3>
                      <p className="text-gray-700 mb-4">Think of a situation where you need something. Write a clear, specific request:</p>
                      <Textarea
                        placeholder="Example: 'Would you be willing to...'"
                        className="border-yellow-600 min-h-[100px]"
                        style={{ borderRadius: 0 }}
                      />
                    </div>

                    <Card className="mt-8 border-2 border-black bg-gray-50" style={{ borderRadius: 0 }}>
                      <CardHeader>
                        <CardTitle className="text-2xl">Remember: Even Tiny Nuggets Matter</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-700">
                          You don't need to solve everything at once. Finding even a small piece of common ground - 
                          just a little golden nugget - is progress. Keep requesting, keep listening, keep finding 
                          those tiny wins that build connection.
                        </p>
                      </CardContent>
                    </Card>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Phase 5: Non-Feeling Words */}
            {currentPhase === 5 && (
              <div className="space-y-8">
                <Card className="border-4 border-yellow-600 bg-white" style={{ borderRadius: 0, boxShadow: '12px 12px 0px rgba(217,119,6,0.2)' }}>
                  <CardHeader className="bg-yellow-50">
                    <CardTitle className="text-3xl text-yellow-800">
                      ⚠️ Non-Feeling Words Alert!
                    </CardTitle>
                    <p className="text-xl text-yellow-800">
                      These words imply someone else is doing something to you. 
                      When you claim the agency of your mind, you gain a superpower!
                    </p>
                  </CardHeader>
                  <CardContent className="p-8">
                    
                    <div>
                      <h3 className="text-2xl font-bold mb-6 text-yellow-800">Trap Words (Click to see the flash!):</h3>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {nonFeelingWords.map((word) => (
                          <Button
                            key={word}
                            onClick={() => handleNonFeelingClick(word)}
                            className="border-2 border-yellow-600 bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                            style={{ borderRadius: 0 }}
                          >
                            {word}
                          </Button>
                        ))}
                      </div>
                    </div>

                    <Card className="mt-8 border-2 border-black bg-white" style={{ borderRadius: 0 }}>
                      <CardHeader>
                        <CardTitle>Transform Non-Feelings Exercise</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <label className="block text-sm font-bold mb-2">1. Choose a non-feeling word you tend to use:</label>
                          <Input 
                            placeholder="e.g., rejected, ignored, betrayed..."
                            className="border-yellow-600"
                            style={{ borderRadius: 0 }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-bold mb-2">2. Describe a situation where you use this word:</label>
                          <Textarea 
                            placeholder="When did you feel this way?"
                            className="border-yellow-600"
                            style={{ borderRadius: 0 }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-bold mb-2">3. What are the actual feelings you're having? (use red - feelings)</label>
                          <Input 
                            placeholder="sad, angry, frustrated..."
                            className="border-red-600"
                            style={{ borderRadius: 0 }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-bold mb-2">4. What are the actual needs involved? (use green - needs)</label>
                          <Input 
                            placeholder="connection, respect, understanding..."
                            className="border-green-600"
                            style={{ borderRadius: 0 }}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Phase 4: Page 17 OFNR Practice Sheet */}
            {currentPhase === 4 && (
              <div className="space-y-8">
                <Card className="border-4 border-yellow-500 bg-white" style={{ borderRadius: 0, boxShadow: '12px 12px 0px rgba(234,179,8,0.2)' }}>
                  <CardHeader className="bg-yellow-50">
                    <CardTitle className="text-3xl flex items-center gap-3">
                      <Target className="w-8 h-8 text-yellow-600" />
                      OFNR Practice Sheet (Page 17)
                    </CardTitle>
                    <p className="text-xl text-yellow-700">
                      Express honestly ↔ Listen empathetically - Finding common ground is gold!
                    </p>
                  </CardHeader>
                  <CardContent className="p-8">
                    
                    <div className="grid md:grid-cols-2 gap-8">
                      {/* Expressing Myself Honestly */}
                      <Card className="border-4 border-blue-500 bg-blue-50" style={{ borderRadius: 0 }}>
                        <CardHeader>
                          <CardTitle className="text-xl text-blue-800">
                            Expressing myself honestly
                          </CardTitle>
                          <p className="text-sm text-blue-700">Through statements without blaming or criticizing</p>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <h4 className="font-bold text-blue-800 mb-2">O - Observation</h4>
                            <p className="text-sm text-blue-700 mb-2">When I observe (see, hear, remember, imagine)...</p>
                            <Textarea
                              placeholder="Reflecting my observations of what I see, hear, etc. that does or does not contribute to my well-being"
                              className="border-blue-500 min-h-[60px] text-sm"
                              style={{ borderRadius: 0 }}
                            />
                          </div>
                          
                          <div>
                            <h4 className="font-bold text-red-800 mb-2">F - Feeling</h4>
                            <p className="text-sm text-red-700 mb-2">I feel...</p>
                            <Textarea
                              placeholder="My emotions or bodily sensations related to what I observe rather than my thoughts"
                              className="border-red-500 min-h-[60px] text-sm"
                              style={{ borderRadius: 0 }}
                            />
                          </div>
                          
                          <div>
                            <h4 className="font-bold text-green-800 mb-2">N - Need</h4>
                            <p className="text-sm text-green-700 mb-2">Because I need/value...</p>
                            <Textarea
                              placeholder="What I need or value that causes my feelings. Needs are distinct from preferences or specific actions."
                              className="border-green-500 min-h-[60px] text-sm"
                              style={{ borderRadius: 0 }}
                            />
                          </div>
                          
                          <div>
                            <h4 className="font-bold text-yellow-600 mb-2">R - Request</h4>
                            <p className="text-sm text-yellow-700 mb-2">Would you be willing to...</p>
                            <Textarea
                              placeholder="Clearly requesting that which would enrich my life without demanding. The concrete actions I would like taken."
                              className="border-yellow-500 min-h-[60px] text-sm"
                              style={{ borderRadius: 0 }}
                            />
                          </div>
                        </CardContent>
                      </Card>

                      {/* Listening Empathetically */}
                      <Card className="border-4 border-purple-500 bg-purple-50" style={{ borderRadius: 0 }}>
                        <CardHeader>
                          <CardTitle className="text-xl text-purple-800">
                            Listening empathetically
                          </CardTitle>
                          <p className="text-sm text-purple-700">Through asking, and receiving without hearing blame or criticism</p>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <h4 className="font-bold text-blue-800 mb-2">O - Observation</h4>
                            <p className="text-sm text-blue-700 mb-2">When you observe (see, hear, remember, imagine)...</p>
                            <Textarea
                              placeholder="Reflecting what I think you observe: see, hear, etc. that does or does not contribute to your well-being"
                              className="border-blue-500 min-h-[60px] text-sm"
                              style={{ borderRadius: 0 }}
                            />
                          </div>
                          
                          <div>
                            <h4 className="font-bold text-red-800 mb-2">F - Feeling</h4>
                            <p className="text-sm text-red-700 mb-2">Do you feel...?</p>
                            <Textarea
                              placeholder="What I think may be your emotions or bodily sensations related to what you observe rather than your thoughts"
                              className="border-red-500 min-h-[60px] text-sm"
                              style={{ borderRadius: 0 }}
                            />
                          </div>
                          
                          <div>
                            <h4 className="font-bold text-green-800 mb-2">N - Need</h4>
                            <p className="text-sm text-green-700 mb-2">Because you need/value...</p>
                            <Textarea
                              placeholder="What I think you need or value that causes your feelings. Needs are distinct from preferences or specific actions."
                              className="border-green-500 min-h-[60px] text-sm"
                              style={{ borderRadius: 0 }}
                            />
                          </div>
                          
                          <div>
                            <h4 className="font-bold text-yellow-600 mb-2">R - Request</h4>
                            <p className="text-sm text-yellow-700 mb-2">Would you like...?</p>
                            <Textarea
                              placeholder="Empathically receiving that which would enrich your life without hearing any demands. Sometimes unspoken when offering empathy."
                              className="border-yellow-500 min-h-[60px] text-sm"
                              style={{ borderRadius: 0 }}
                            />
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    <Card className="mt-8 border-4 border-black bg-gray-50" style={{ borderRadius: 0 }}>
                      <CardContent className="p-6 text-center">
                        <p className="text-lg font-bold mb-4">
                          ✨ The Magic: When two people in conflict can stay empathically connected to each other's feelings and needs, solutions naturally arise! ✨
                        </p>
                        <Button
                          className="bg-black text-white hover:bg-gray-800 px-8 py-3"
                          style={{ borderRadius: 0 }}
                          onClick={() => {
                            const userId = localStorage.getItem('workbook_user_id');
                            if (userId) {
                              toast.success('OFNR practice saved to your journal!');
                            } else {
                              toast.info('Create an account to save your OFNR practice!');
                            }
                          }}
                        >
                          💾 Save This OFNR Practice
                        </Button>
                      </CardContent>
                    </Card>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Phase 5: Non-Feeling Words (Tan) */}
            {currentPhase === 5 && (
              <div className="space-y-8">
                <Card className="border-4 border-amber-700 bg-white" style={{ borderRadius: 0, boxShadow: '12px 12px 0px rgba(180,83,9,0.2)' }}>
                  <CardHeader className="bg-amber-100">
                    <CardTitle className="text-3xl text-amber-900">
                      ⚠️ Non-Feeling Words Alert! (Page 27)
                    </CardTitle>
                    <p className="text-xl text-amber-800">
                      These words imply someone else is doing something to you. 
                      When you claim the agency of your mind, you gain a superpower!
                    </p>
                  </CardHeader>
                  <CardContent className="p-8">
                    
                    <div className="mb-8">
                      <h3 className="text-2xl font-bold mb-6 text-amber-900">⚠️ Trap Words - Click to See the Flash & Select!</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {ofnrData.nonFeelingWords.map((word) => (
                          <Button
                            key={word}
                            onClick={() => {
                              setFlashTan(true);
                              setTimeout(() => setFlashTan(false), 500);
                              
                              // Add to selection
                              if (selectedNonFeelings.includes(word)) {
                                setSelectedNonFeelings(prev => prev.filter(w => w !== word));
                              } else {
                                setSelectedNonFeelings(prev => [...prev, word]);
                              }
                            }}
                            variant={selectedNonFeelings.includes(word) ? "default" : "outline"}
                            className={`border-2 border-amber-700 text-sm py-3 ${
                              selectedNonFeelings.includes(word)
                                ? 'bg-amber-700 text-white'
                                : 'bg-amber-100 text-amber-900 hover:bg-amber-200'
                            }`}
                            style={{ borderRadius: 0 }}
                          >
                            {word}
                          </Button>
                        ))}
                      </div>
                      
                      {/* Selected Non-Feelings Display */}
                      {selectedNonFeelings.length > 0 && (
                        <Card className="mt-6 bg-amber-50 border-4 border-amber-700" style={{ borderRadius: 0 }}>
                          <CardContent className="p-4">
                            <h4 className="text-lg font-bold text-amber-900 mb-3">⚠️ Trap Words You Selected:</h4>
                            <div className="flex flex-wrap gap-2 mb-4">
                              {selectedNonFeelings.map((word) => (
                                <Badge key={word} className="bg-amber-700 text-white text-base px-3 py-2">
                                  {word}
                                </Badge>
                              ))}
                            </div>
                            <div className="text-center">
                              <Button
                                className="bg-amber-700 text-white hover:bg-amber-800"
                                style={{ borderRadius: 0 }}
                                onClick={() => {
                                  const userId = localStorage.getItem('workbook_user_id');
                                  if (userId) {
                                    toast.success('Non-feeling words awareness saved! You\'re reclaiming your power.');
                                  } else {
                                    toast.info('Create an account to save your trap word awareness!');
                                  }
                                }}
                              >
                                💾 Save Trap Word Awareness
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>

                    <Card className="border-4 border-black bg-white" style={{ borderRadius: 0 }}>
                      <CardHeader>
                        <CardTitle className="text-2xl">Transform Non-Feelings Exercise</CardTitle>
                        <p className="text-base text-gray-600">Turn trap words into empowering language</p>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div>
                          <label className="block text-sm font-bold mb-2">1. Choose a non-feeling word you tend to use:</label>
                          <Input 
                            placeholder="e.g., rejected, ignored, betrayed..."
                            className="border-amber-700"
                            style={{ borderRadius: 0 }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-bold mb-2">2. Describe a situation where you use this word:</label>
                          <Textarea 
                            placeholder="When did you feel this way? What happened?"
                            className="border-amber-700 min-h-[80px]"
                            style={{ borderRadius: 0 }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-bold mb-2">3. What are the actual feelings you're having? (🟥 red - feelings)</label>
                          <Input 
                            placeholder="sad, angry, frustrated, hurt..."
                            className="border-red-600"
                            style={{ borderRadius: 0 }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-bold mb-2">4. What are the actual needs involved? (🟩 green - needs)</label>
                          <Input 
                            placeholder="connection, respect, understanding, safety..."
                            className="border-green-600"
                            style={{ borderRadius: 0 }}
                          />
                        </div>
                        
                        <Card className="bg-green-50 border-2 border-green-600" style={{ borderRadius: 0 }}>
                          <CardContent className="p-4 text-center">
                            <p className="text-lg font-bold text-green-800 mb-2">
                              🌟 When you claim the agency of your mind, you gain a superpower! 🌟
                            </p>
                            <p className="text-base text-green-700">
                              Transform blame into self-connection and empowerment
                            </p>
                          </CardContent>
                        </Card>
                      </CardContent>
                    </Card>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Practice Scenarios - Available on Every Page */}
            <div className="mt-12 max-w-4xl mx-auto">
              <Card className="border-4 border-purple-600 bg-purple-50" style={{ borderRadius: 0 }}>
                <CardHeader>
                  <CardTitle className="text-2xl text-purple-800">🎭 Practice Scenarios</CardTitle>
                  <p className="text-gray-700">Generated practice situations for real-world application</p>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="mb-4 p-4 bg-white border-2 border-purple-600" style={{ borderRadius: 0 }}>
                    <p className="text-lg italic text-purple-800 mb-3">
                      "{scenario}"
                    </p>
                    <div className="flex gap-3">
                      <Button 
                        onClick={generateScenario}
                        className="bg-purple-600 text-white hover:bg-purple-700"
                        style={{ borderRadius: 0 }}
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        New Scenario
                      </Button>
                      <Button
                        variant="outline"
                        className="border-purple-600 text-purple-600"
                        style={{ borderRadius: 0 }}
                        onClick={() => {
                          const userId = localStorage.getItem('workbook_user_id');
                          if (userId) {
                            toast.success('Scenario saved to your practice journal!');
                          } else {
                            toast.info('Create an account to save scenarios!');
                          }
                        }}
                      >
                        💾 Save for Practice
                      </Button>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-base text-purple-700 mb-2">
                      <strong>Practice Tip:</strong> Use this scenario to work through the OFNR process
                    </p>
                    <p className="text-sm text-gray-600 mb-4">
                      What would you observe? How would you feel? What needs arise? What would you request?
                    </p>
                    
                    {/* M.ai.ry Coaching */}
                    <Button
                      onClick={async () => {
                        try {
                          // Get coaching from M.ai.ry using Emergent LLM
                          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/coaching`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              type: 'ofnr_practice_coaching',
                              scenario: scenario,
                              phase: currentPhase,
                              selectedFeelings: selectedSatisfiedFeelings.concat(selectedUnsatisfiedFeelings),
                              selectedNeeds: selectedNeeds,
                              selectedNonFeelings: selectedNonFeelings,
                              userContext: {
                                currentPhase: phases[currentPhase]?.title,
                                totalSelections: selectedFeelings.length + selectedNeeds.length + selectedNonFeelings.length
                              }
                            })
                          });
                          
                          if (response.ok) {
                            const data = await response.json();
                            toast.success(`M.ai.ry: ${data.coaching}`, { duration: 8000 });
                          } else {
                            toast.info("Practice this scenario using the OFNR steps you've learned!");
                          }
                        } catch (error) {
                          toast.info("Work through this scenario: What do you observe, feel, need, and request?");
                        }
                      }}
                      className="bg-purple-600 text-white hover:bg-purple-700"
                      style={{ borderRadius: 0 }}
                    >
                      💫 Get M.ai.ry Coaching
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
            {/* Phase 6: Games & Tools - Advanced Practices */}
            {currentPhase === 6 && (
              <div className="space-y-8">
                <Card 
                  className="border-4 border-purple-600 bg-white" 
                  style={{ 
                    borderRadius: 0, 
                    boxShadow: '12px 12px 0px rgba(147,51,234,0.2)',
                    backgroundImage: `url('https://customer-assets.emergentagent.com/job_belonging-sessions/artifacts/femlgvnm_ChatGPT%20Image%20Sep%2011%2C%202025%20at%2012_35_28%20AM.jpeg')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    position: 'relative'
                  }}
                >
                  <div style={{ 
                    position: 'absolute', 
                    inset: 0, 
                    backgroundColor: 'rgba(255,255,255,0.95)' 
                  }}></div>
                  <CardHeader className="bg-purple-50 relative z-10" style={{ backgroundColor: 'rgba(147,51,234,0.1)' }}>
                    <CardTitle className="text-3xl flex items-center gap-3">
                      <Star className="w-8 h-8 text-purple-600" />
                      🎮 Games & Tools: Advanced NVC Practices
                    </CardTitle>
                    <p className="text-xl text-purple-800">
                      Game-like experiences for deeper mastery of compassionate communication
                    </p>
                  </CardHeader>
                  <CardContent className="p-8 relative z-10">
                    
                    {/* Secret History Game - Featured */}
                    <Card className="mb-8 border-4 border-blue-600 bg-blue-50" style={{ borderRadius: 0 }}>
                      <CardHeader>
                        <CardTitle className="text-2xl text-blue-800">🌍 Secret History of Humankind</CardTitle>
                        <p className="text-base text-blue-700">Multiplayer adventure game exploring compassionate communication in beta</p>
                      </CardHeader>
                      <CardContent>
                        <p className="text-base text-gray-700 mb-4">
                          Explore the secret history of human connection and communication through an immersive multiplayer experience.
                        </p>
                        <a href="https://playsecrethistory.com" target="_blank" rel="noopener noreferrer">
                          <Button 
                            className="w-full bg-blue-600 text-white hover:bg-blue-700 py-4 text-lg"
                            style={{ borderRadius: 0 }}
                          >
                            🕹️ Play Secret History Game
                          </Button>
                        </a>
                      </CardContent>
                    </Card>

                    <div className="grid md:grid-cols-2 gap-8">
                      
                      {/* Working with Conflict Game */}
                      <Card className="border-3 border-red-500 bg-red-50" style={{ borderRadius: 0 }}>
                        <CardHeader>
                          <CardTitle className="text-xl text-red-800">🔥 Conflict Navigator</CardTitle>
                          <p className="text-sm text-red-700">30-step process to transform conflict into connection</p>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <p className="text-sm font-bold">Step 1: Notice your jackal show. Enjoy it if you want to!</p>
                            <p className="text-sm font-bold">Step 2: Give yourself empathy for jackal things they said/did</p>
                            <p className="text-sm font-bold">Step 3: Translate jackal thoughts into feelings and needs</p>
                          </div>
                          <Button 
                            className="w-full mt-4 bg-red-600 text-white hover:bg-red-700"
                            style={{ borderRadius: 0 }}
                            onClick={() => {
                              const audio = new Audio('data:audio/wav;base64,UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAAxMTExMTExMTExMTExMTEx');
                              audio.volume = 0.3;
                              audio.play().catch(() => {});
                              toast.success('Conflict Navigator: Track your progress through the 30 steps!');
                            }}
                          >
                            🎯 Start Conflict Resolution Game
                          </Button>
                        </CardContent>
                      </Card>

                      {/* Three Areas of Attention */}
                      <Card className="border-3 border-yellow-500 bg-yellow-50" style={{ borderRadius: 0 }}>
                        <CardHeader>
                          <CardTitle className="text-xl text-yellow-800">🧠 Mind Awareness Game</CardTitle>
                          <p className="text-sm text-yellow-700">Track thoughts, feelings, reactions in real-time</p>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            <p className="text-sm">🦎 <strong>Lizard Legacy:</strong> Safety, sustenance, sex</p>
                            <p className="text-sm">🐾 <strong>Furry Mammal:</strong> Bonding, status, play</p>
                            <p className="text-sm">🤖 <strong>Computer Brain:</strong> Meaning, morality</p>
                            <p className="text-sm">🐬 <strong>Higher Porpoise:</strong> Wholeness, contribution</p>
                          </div>
                          <Button 
                            className="w-full mt-4 bg-yellow-600 text-white hover:bg-yellow-700"
                            style={{ borderRadius: 0 }}
                            onClick={() => {
                              const audio = new Audio('data:audio/wav;base64,UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAA0NDQ0NDQ0NDQ0NDQ0NDQ0NDQ0');
                              audio.volume = 0.35;
                              audio.play().catch(() => {});
                              toast.success('Quadrune Brain Explorer: Notice which part of your brain is active!');
                            }}
                          >
                            🧠 Explore Your Mind
                          </Button>
                        </CardContent>
                      </Card>

                      {/* Gratitude Game */}
                      <Card className="border-3 border-green-500 bg-green-50" style={{ borderRadius: 0 }}>
                        <CardHeader>
                          <CardTitle className="text-xl text-green-800">💚 Gratitude Builder</CardTitle>
                          <p className="text-sm text-green-700">Turn daily experiences into fuel for compassion</p>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 text-sm">
                            <p>✨ <strong>Daily Gratitude Journal</strong></p>
                            <p>🎁 <strong>Giving & Receiving Practice</strong></p>
                            <p>🌟 <strong>Celebration Exercises</strong></p>
                            <p>💖 <strong>Take in the Good Practice</strong></p>
                          </div>
                          <Button 
                            className="w-full mt-4 bg-green-600 text-white hover:bg-green-700"
                            style={{ borderRadius: 0 }}
                            onClick={() => {
                              const audio = new Audio('data:audio/wav;base64,UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAAzMzMzMzMzMzMzMzMzMzMzMzMz');
                              audio.volume = 0.4;
                              audio.play().catch(() => {});
                              toast.success('Gratitude practices fuel compassionate consciousness!');
                            }}
                          >
                            🙏 Practice Gratitude
                          </Button>
                        </CardContent>
                      </Card>

                      {/* I Made It Mean Worksheet */}
                      <Card className="border-3 border-blue-500 bg-blue-50" style={{ borderRadius: 0 }}>
                        <CardHeader>
                          <CardTitle className="text-xl text-blue-800">💭 Meaning Maker</CardTitle>
                          <p className="text-sm text-blue-700">Catch your interpretations and choose your responses</p>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <Input 
                              placeholder="Somebody said to me..." 
                              className="border-blue-500"
                              style={{ borderRadius: 0 }}
                            />
                            <Input 
                              placeholder="...and I made it mean..."
                              className="border-blue-500" 
                              style={{ borderRadius: 0 }}
                            />
                          </div>
                          <Button 
                            className="w-full mt-4 bg-blue-600 text-white hover:bg-blue-700"
                            style={{ borderRadius: 0 }}
                            onClick={() => {
                              const audio = new Audio('data:audio/wav;base64,UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAAyNjY2NjY2NjY2NjY2NjY2NjY2');
                              audio.volume = 0.3;
                              audio.play().catch(() => {});
                              toast.success('Notice what meaning you create - you have choice!');
                            }}
                          >
                            🔍 Explore Meaning-Making
                          </Button>
                        </CardContent>
                      </Card>

                      {/* Intimacy Questions Game */}
                      <Card className="border-3 border-pink-500 bg-pink-50" style={{ borderRadius: 0 }}>
                        <CardHeader>
                          <CardTitle className="text-xl text-pink-800">💕 Connection Cards</CardTitle>
                          <p className="text-sm text-pink-700">46 intimacy-building questions for deeper relating</p>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 text-sm">
                            <p>"What makes you feel alive?"</p>
                            <p>"What's something missing in your life?"</p>
                            <p>"Who are you most grateful for?"</p>
                          </div>
                          <Button 
                            className="w-full mt-4 bg-pink-600 text-white hover:bg-pink-700"
                            style={{ borderRadius: 0 }}
                            onClick={() => {
                              const audio = new Audio('data:audio/wav;base64,UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAAzNzc3NzM3NzM3NzM3NzM3Nzc3');
                              audio.volume = 0.35;
                              audio.play().catch(() => {});
                              toast.success('Draw a card and deepen connection!');
                            }}
                          >
                            🎴 Draw Connection Card
                          </Button>
                        </CardContent>
                      </Card>

                      {/* Fear of Feelings */}
                      <Card className="border-3 border-orange-500 bg-orange-50" style={{ borderRadius: 0 }}>
                        <CardHeader>
                          <CardTitle className="text-xl text-orange-800">😱 Truth Courage Builder</CardTitle>
                          <p className="text-sm text-orange-700">Transform fear of feeling into authentic expression</p>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 text-sm">
                            <p><strong>Layer 1:</strong> I'm protecting them</p>
                            <p><strong>Layer 2:</strong> I'm protecting myself</p>
                            <p><strong>Question:</strong> What am I afraid will happen if I truly express myself?</p>
                          </div>
                          <Button 
                            className="w-full mt-4 bg-orange-600 text-white hover:bg-orange-700"
                            style={{ borderRadius: 0 }}
                            onClick={() => {
                              const audio = new Audio('data:audio/wav;base64,UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAA1NTU1NTU1NTU1NTU1NTU1NTU1');
                              audio.volume = 0.3;
                              audio.play().catch(() => {});
                              toast.success('Risk the dreaded experience and reap the rewards!');
                            }}
                          >
                            💪 Build Truth Courage
                          </Button>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Advanced Practice Selection */}
                    <Card className="mt-8 border-4 border-black bg-white" style={{ borderRadius: 0 }}>
                      <CardHeader>
                        <CardTitle className="text-2xl text-center">Choose Your Adventure</CardTitle>
                        <p className="text-center text-gray-600">Select a practice area to explore deeply</p>
                      </CardHeader>
                      <CardContent>
                        <div className="grid md:grid-cols-3 gap-4">
                          <Button 
                            className="bg-red-600 text-white hover:bg-red-700 py-6"
                            style={{ borderRadius: 0 }}
                            onClick={() => setActiveGame('conflict')}
                          >
                            🔥 Conflict Navigator (30 steps)
                          </Button>
                          <Button 
                            className="bg-green-600 text-white hover:bg-green-700 py-6"
                            style={{ borderRadius: 0 }}
                            onClick={() => setActiveGame('gratitude')}
                          >
                            💚 Gratitude Builder Game
                          </Button>
                          <Button 
                            className="bg-blue-600 text-white hover:bg-blue-700 py-6"
                            style={{ borderRadius: 0 }}
                            onClick={() => setActiveGame('brain')}
                          >
                            🧠 Quadrune Brain Explorer
                          </Button>
                          <Button 
                            className="bg-pink-600 text-white hover:bg-pink-700 py-6"
                            style={{ borderRadius: 0 }}
                            onClick={() => setActiveGame('connection')}
                          >
                            💕 Connection Cards
                          </Button>
                          <Button 
                            className="bg-indigo-600 text-white hover:bg-indigo-700 py-6"
                            style={{ borderRadius: 0 }}
                            onClick={() => setActiveGame('meaning')}
                          >
                            💭 Meaning Maker
                          </Button>
                          <Button 
                            className="bg-orange-600 text-white hover:bg-orange-700 py-6"
                            style={{ borderRadius: 0 }}
                            onClick={() => setActiveGame('courage')}
                          >
                            💪 Truth Courage Builder
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </CardContent>
                </Card>
              </div>
            )}

          </div>
        </main>

        {/* Bottom Navigation & Contact */}
        <footer className="py-8 px-4 bg-black text-white">
          <div className="max-w-6xl mx-auto">
            {currentPhase > 0 && (
              <div className="flex justify-between items-center mb-8">
                <Button 
                  onClick={() => setCurrentPhase(Math.max(0, currentPhase - 1))}
                  variant="outline" 
                  className="border-white text-white hover:bg-white hover:text-black"
                  style={{ borderRadius: 0 }}
                >
                  ← Previous
                </Button>
                
                <p className="text-xl font-bold text-center">
                  Think about these tools during conflict conversations!<br/>
                  <span className="text-base font-normal">The test is: Can you apply this in real-world situations?</span>
                </p>
                
                <Button 
                  onClick={() => setCurrentPhase(Math.min(5, currentPhase + 1))}
                  variant="outline" 
                  className="border-white text-white hover:bg-white hover:text-black"
                  style={{ borderRadius: 0 }}
                >
                  Next →
                </Button>
              </div>
            )}

            {/* Contact Form */}
            <div id="contact-form" className="max-w-2xl mx-auto">
              <Card className="border-2 border-white bg-gray-900" style={{ borderRadius: 0 }}>
                <CardHeader>
                  <CardTitle className="text-white text-center">Connect with The Belonging Sessions</CardTitle>
                  <p className="text-gray-300 text-center">
                    Questions about this tool? Want to join our community? We'd love to hear from you.
                  </p>
                </CardHeader>
                <CardContent>
                  <form className="space-y-4" onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    try {
                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact-requests`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          name: formData.get('name'),
                          email: formData.get('email'),
                          type: 'ofnr_trainer',
                          comments: formData.get('message'),
                          interest: 'OFNR Trainer + Belonging Sessions'
                        })
                      });
                      
                      if (!response.ok) throw new Error('Failed');
                      toast.success("Thank you! We'll be in touch about The Belonging Sessions.");
                      e.target.reset();
                    } catch (error) {
                      toast.error('Please try again');
                    }
                  }}>
                    <div className="grid md:grid-cols-2 gap-4">
                      <Input
                        name="name"
                        placeholder="Your name"
                        required
                        className="border-white bg-gray-800 text-white"
                        style={{ borderRadius: 0 }}
                      />
                      <Input
                        name="email"
                        type="email"
                        placeholder="Your email"
                        required
                        className="border-white bg-gray-800 text-white"
                        style={{ borderRadius: 0 }}
                      />
                    </div>
                    <Textarea
                      name="message"
                      placeholder="Your message or questions about compassionate communication..."
                      required
                      className="border-white bg-gray-800 text-white min-h-[100px]"
                      style={{ borderRadius: 0 }}
                    />
                    <Button 
                      type="submit"
                      className="w-full bg-white text-black hover:bg-gray-200 py-3 font-bold"
                      style={{ borderRadius: 0 }}
                    >
                      Send Message & Join The Belonging Sessions Community
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
            
            <div className="text-center mt-6 text-gray-400">
              <p>© 2025 The Belonging Sessions • Building Compassionate Connection</p>
            </div>
          </div>
        </footer>

      </div>
    </div>
  );
}

export default OFNRTrainer;